import { actionVerb } from './complianceActions';
import { supabase } from '../supabaseClient';

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// British deadline format — "4 Jun 2026, 15:45". Falls back to the raw
// value if it isn't a parseable date. Used by the brief notification /
// reminder / update emails so the deadline reads as a human sentence
// instead of the ISO timestamp the call sites pass through.
const EMAIL_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtEmailDeadline = (value) => {
  if (value == null || value === '') return '';
  // Already a pre-formatted string (no ISO 'T') — pass through unchanged
  // so call sites that format their own deadline don't get double-handled.
  if (typeof value === 'string' && !/T\d{2}:/.test(value)) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${EMAIL_MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};

// Defeating Outlook on the Web's email auto-linker, layered:
// 1. If the value looks like an email, wrap in a real mailto: anchor — OWA
//    treats the value as already-linked and stops trying to re-style it.
// 2. Inject zero-width spaces around @ and . in the displayed text so any
//    secondary detector that runs over the rendered DOM doesn't match either.
// 3. Inline colour/decoration with !important to beat any client stylesheet
//    (the mailto anchor itself would otherwise pick up OWA's default link blue).
const ZWSP = '​';
const breakEmailDetect = (s) =>
  s.replace(/@/g, `${ZWSP}@${ZWSP}`).replace(/\./g, `${ZWSP}.${ZWSP}`);

const plainText = (value, color = '#F9FCF1', font = "'Metropolis',Helvetica,Arial,sans-serif") => {
  const escaped = escapeHtml(value);
  const display = breakEmailDetect(escaped);
  const styles = `color:${color} !important;text-decoration:none !important;font-family:${font};font-weight:inherit;`;
  if (typeof value === 'string' && value.includes('@')) {
    return `<a href="mailto:${escaped}" style="${styles}">${display}</a>`;
  }
  return `<span style="${styles}">${display}</span>`;
};

// Turbo mascot for admin-facing emails. Rendered above the CTA when
// the email factory passes `showTurbo: true` to alchemyShell().
//
// Served as PNGs (not SVG): email clients render SVG inconsistently —
// Outlook strips it entirely, and even clients that accept inline SVG
// scale it unpredictably. PNG with width/height attrs is the boring
// option that renders the same everywhere.
//
// Three sizes ship: 44 (1x), 88 (2x), 132 (3x). Clients that honour
// srcset (Apple Mail, iOS Mail, Gmail web on retina, modern Yahoo)
// pick the right size for the device. Clients that don't (Outlook
// desktop, Gmail mobile in some configurations) fall back to the 44
// base which renders crisply at the 44×44 display size.
//
// Files are versioned (otto-44.v1.png) so a future Otto refresh can
// bump to v2 and force email clients to re-fetch — they cache image
// bodies aggressively by URL.
//
// The absolute URL origin is derived from the email's existing ctaUrl
// so production / staging / localhost each pull the image from their
// own host without us having to thread an appUrl param through
// every factory. The assets live in public/ alongside the rest of
// the CRA static files; if they're missing the email still sends
// (alt text "Supply Pilot" surfaces in place of the image). Otto is
// admin-only — this mascot renders only in alchemyShell; supplier
// emails use the mascot-free supplierShell.
const OTTO_PNG_PATHS = {
  src: '/otto-44.v1.png',
  src2x: '/otto-88.v1.png',
  src3x: '/otto-132.v1.png',
};
const ottoUrlsFromCta = (ctaUrl) => {
  if (!ctaUrl) return null;
  try {
    const origin = new URL(ctaUrl).origin;
    return {
      src: `${origin}${OTTO_PNG_PATHS.src}`,
      src2x: `${origin}${OTTO_PNG_PATHS.src2x}`,
      src3x: `${origin}${OTTO_PNG_PATHS.src3x}`,
    };
  } catch (err) {
    return null;
  }
};



// Token map for the alchemy accent. The accent only ever appears in two
// places — the short divider line below the header, and the CTA pill if
// it's the outline style. Eyebrow text, F-badge, and credential labels
// stay green regardless of accent (see email_family_full.html mock).
const ACCENT_HEX = { green: '#92D400', amber: '#E89A2E' };









// Alchemy admin-facing shell:
// - preheader, bodyHtml, ctaLabel, ctaUrl
// - showTurbo: boolean (default false) — adds Otto beneath the CTA
// NEVER use for supplier-portal emails. Suppliers get supplierShell —
// see [[project-email-shell-architecture]].
//
// This list used to name `accent`, `ctaStyle` and `footerText` too. They were
// knobs on the retired dark three-tier design and the V2 card has no
// equivalent — but 12 templates went on passing them, and the shell went on
// silently dropping them, for months. Two carried real information that never
// reached a single recipient ("Compliance alerts cannot be disabled",
// "Safety incident alerts cannot be disabled"); those sentences now live in
// the BODY of their emails, which is where a reader would look anyway.
//
// Deleted rather than implemented, deliberately. A per-template footer
// override is precisely how the family drifts apart again, and every admin
// email having the same footer is the point.
// Adapter → the LIGHT admin shell (alchemyShellLight). Every admin/internal
// email now renders on the white card, matching the supplier + welcome/password
// emails. The legacy dark-card bodies emphasise with white #F9FCF1 and inline
// acid #92D400 on small text — recolour those to ink #131313 / deep-green
// #009159 so they read on white. ctaStyle is dropped (the light CTA is a single
// bulletproof button); showTurbo maps to the light Otto slot.
// ============================================================================
// SHELL TOPOLOGY — read this before concluding the emails are forked
// ============================================================================
// There are only TWO live shells, and they render the same card:
//
//   alchemyShell   → adapter → alchemyShellV2    (admin / internal)
//   supplierShell  → adapter → supplierShellV2   (supplier-facing)
//
// The V2 shells ARE the design: white body (#FFFFFF), black mastheadV2 band
// (#131313) at the top. The non-V2 ones are thin adapters that recolour
// legacy DARK-authored bodies (white #F9FCF1 emphasis → ink, dark panels →
// light tint) before handing them to V2. So a template on alchemyShell and one
// on alchemyShellV2 look identical; the difference is only which idiom its
// BODY was written in.
//
// Consequence, learned the slow way on 2026-08-13: "some admin emails have a
// black header and some don't" was NOT a shell split. Every admin email gets
// mastheadV2 — except the one template that was on alchemyShellLight, which
// renders no masthead at all. That was supplierAcceptedEmail. One email, since
// moved onto alchemyShell.
//
// DEAD CODE, deliberately left in place but unused (the build warns on them):
//   alchemyShellLight, supplierShellLight, briefDetailsTableLight,
//   renderHead, renderHeader, renderDivider, renderBody, renderOtto,
//   renderCta, renderFooter
// They are leftovers from an earlier dark-card design. Nothing calls them and
// nothing exports them. They make this file LOOK far more forked than it is —
// if you're deciding "which shell should we standardise on", the answer is
// already "the V2 pair", and these are not candidates.
// ============================================================================
const alchemyShell = ({ preheader, bodyHtml, ctaLabel, ctaUrl, showTurbo = false }) => {
  const lightBody = (bodyHtml || '')
    .replace(/#F9FCF1/gi, '#131313')                        // white emphasis/mono text → ink
    .replace(/color:#92D400/gi, 'color:#009159')            // inline acid small text → deep green
    .replace(/background:#1a1a1a/gi, 'background:#F7F9F0')  // dark emphasis/credential panels → light tint
    .replace(/1px solid #2a2a2a/gi, '1px solid #E6EADB')    // their dark borders → light hairline
    .replace(/#DADCCD/gi, '#4C4E42');                       // dark-panel body text → ink
  const bodyInner = `${lightBody}${ctaLabel && ctaUrl ? `\n${ctaV2({ label: ctaLabel, url: ctaUrl })}` : ''}`;
  return alchemyShellV2({ preheader, bodyInner, ctaUrl, showOtto: showTurbo });
};

// Supplier-portal shell. Deliberately stripped of Alchemy's accent /
// ctaStyle / Turbo knobs — supplier emails are Fusion-professional, no
// mascot, always green-solid CTA. If you ever find yourself reaching
// for Turbo from a supplier-facing call site, that's the wrong shell.
// Stage 2 (BRIEF-25) — every supplier email now renders on the v2 light card,
// matching the welcome + password design. This shell is a thin adapter over
// supplierShellV2 so the 16 legacy call sites (compliance / brief / award /
// activation emails) upgrade without touching each one:
//   • the legacy bodies emphasise with white #F9FCF1 for the OLD dark card —
//     recolour to dark ink (#131313) so they read on white. Uncoloured <p>
//     inherit the v2 card's #4C4E42 body ink automatically.
//   • acid #92D400 was used inline on small body text in a couple of bodies
//     (poor contrast on white) — swap those to deep-green #009159.
//   • the CTA moves inside the body via the bulletproof ctaV2 button.
// footerText is intentionally dropped: v2 uses the standard "Need a hand?"
// footer for every supplier email.
const supplierShell = ({ preheader, bodyHtml, ctaLabel, ctaUrl, footerText }) => {
  const lightBody = (bodyHtml || '')
    .replace(/#F9FCF1/gi, '#131313')
    .replace(/color:#92D400/gi, 'color:#009159');
  const bodyInner = `${lightBody}${ctaLabel && ctaUrl ? `\n${ctaV2({ label: ctaLabel, url: ctaUrl })}` : ''}`;
  return supplierShellV2({ preheader, bodyInner, ctaUrl });
};

const originFromUrl = (u) => { try { return new URL(u).origin; } catch (e) { return ''; } };





// Otto (admin-only). Same PNG srcset as the dark theme; descriptive alt.
const renderOttoLight = (ctaUrl) => {
  const urls = ottoUrlsFromCta(ctaUrl);
  if (!urls) return '';
  return `      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
        <tr><td align="center" style="padding:22px 0 0 0;text-align:center;line-height:0;font-size:0;">
        <img src="${escapeHtml(urls.src2x)}" srcset="${escapeHtml(urls.src2x)} 2x, ${escapeHtml(urls.src3x)} 3x" width="64" height="64" alt="Otto, your Pilot Owl" style="display:inline-block;border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;width:64px;height:64px;" />
        </td></tr>
      </table>`;
};






// ============================================================
// Build brief #22 — supplier email design system v2 (mock-anchored).
// Distinguished light layout: 4px acid-green top accent; dark-green
// #004733 masthead with the WHITE reversed Fusion logo + "Supply Pilot ·
// Partner Portal" eyebrow; white body; a credential block; a light-green
// signature panel with icon chips; a bulletproof green CTA; and a
// three-tier no-reply footer BELOW the card. 640px table layout,
// supplier-only (no mascot). Used by the welcome + password emails;
// the other supplier emails adopt it later. Tokens: BRIEF-14 Part D.
// ============================================================
const V2_MONO = "'SF Mono',SFMono-Regular,ui-monospace,Menlo,Consolas,'Courier New',monospace";

// ⚠️ THIS BLOCK IS DUPLICATED VERBATIM in the other module (src/lib/email.js
// ↔ api/_lib/brand-email-shell.cjs) and emailShellParity.test.js diffs them.
// Anything written inside the template literal SHIPS in every email, so keep
// explanations out here: a comment that differs by a word between the two
// files is a real parity failure, which is how the .sp-stat rule was caught.
// .sp-stat stacks side-by-side stat cells on mobile (used by the admin
// digest) rather than letting them squeeze.
const renderHeadV2 = (preheader) => `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<meta name="x-apple-disable-message-reformatting" />
<meta name="color-scheme" content="light only" />
<meta name="supported-color-schemes" content="light only" />
<title>Supply Pilot</title>
<!--[if mso]>
<style type="text/css">table,td,div,p,a {font-family:Helvetica,Arial,sans-serif !important;}</style>
<xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml>
<![endif]-->
<style type="text/css">
table,td {mso-table-lspace:0pt;mso-table-rspace:0pt;border-collapse:collapse;}
img {border:0;display:block;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;}
a {text-decoration:none;}
.sp-preheader {display:none !important;visibility:hidden !important;mso-hide:all !important;font-size:1px !important;line-height:1px !important;max-height:0 !important;max-width:0 !important;opacity:0 !important;overflow:hidden !important;color:#E8ECDB !important;}
@media only screen and (max-width:640px) {
  .sp-card {width:100% !important;}
  .sp-mast {padding:22px 24px !important;}
  .sp-body {padding:28px 24px !important;}
  .sp-cta a {width:100% !important;}
  .sp-stat {display:block !important;width:100% !important;border-right:0 !important;}
}
</style>
</head>
<body style="margin:0;padding:0;background:#E8ECDB;font-family:'Metropolis',Helvetica,Arial,sans-serif;color:#131313;mso-line-height-rule:exactly;-webkit-font-smoothing:antialiased;">
<div class="sp-preheader" style="display:none;max-height:0;overflow:hidden;color:#E8ECDB;">${escapeHtml(preheader || '')}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#E8ECDB" style="background:#E8ECDB;">
  <tr><td align="center" style="padding:40px 16px;">
    <!--[if mso | IE]><table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" width="640"><tr><td><![endif]-->
    <table role="presentation" align="center" width="640" cellpadding="0" cellspacing="0" border="0" class="sp-card" style="max-width:640px;width:100%;background:#FFFFFF;border:1px solid #D4D9C6;border-radius:14px;">
      <tr><td style="height:4px;background:#92D400;font-size:0;line-height:0;mso-line-height-rule:exactly;">&nbsp;</td></tr>`;

const mastheadV2 = (origin, eyebrow = 'Supply Pilot &middot; Partner Portal') => {
  const logo = origin
    ? `<img src="${escapeHtml(origin + '/fusion-email-logo-mast.png')}" width="200" height="52" alt="Fusion" style="display:block;border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;width:200px;height:52px;" />`
    : `<span style="font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:25px;font-weight:800;letter-spacing:1.5px;color:#F9FCF1;">FUSION</span>`;
  // Masthead band = black #131313 (FIX supersedes the earlier dark-green
  // #004733). Gives the green logo maximum contrast + ties the emails to the
  // admin portal's dark chrome. Eyebrow is acid #92D400 (reads well on black;
  // the light-green #B4E2A5 was only needed on the dark-green band).
  return `      <tr><td class="sp-mast" bgcolor="#131313" style="background:#131313;padding:26px 48px;">
        ${logo}
        <div style="font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:2px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#92D400;margin-top:12px;">${eyebrow}</div>
      </td></tr>`;
};

// Credential block — 1px hair + 4px deep-green left border, #FBFCF8 fill.
// Value stays LIVE, copyable, left-aligned; password is mono 18/700.
const credentialBlockV2 = ({ label, value, mono = false }) => `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          <tr><td style="border:1px solid #D4D9C6;border-left:4px solid #009159;border-radius:8px;padding:14px 18px;background:#FBFCF8;">
            <div style="font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:1.2px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#009159;margin-bottom:${mono ? 6 : 4}px;">${escapeHtml(label)}</div>
            <div style="font-family:${mono ? V2_MONO : "'Metropolis',Helvetica,Arial,sans-serif"};font-size:${mono ? 18 : 16}px;font-weight:${mono ? 700 : 600};line-height:24px;mso-line-height-rule:exactly;color:#131313;${mono ? 'letter-spacing:0.5px;' : ''}">${plainText(value, '#131313', mono ? V2_MONO : undefined)}</div>
          </td></tr>
          <!-- Outlook-safe spacer: table margins are ignored by the Word
               engine, so the gap below the credential box is a real spacer
               row (height on td) instead of a table margin-bottom. -->
          <tr><td height="26" style="height:26px;line-height:26px;font-size:0;mso-line-height-rule:exactly;">&nbsp;</td></tr>
        </table>`;

// Light-green signature panel with icon-chip rows. Each chip is a baked
// PNG (circle + icon) so it renders round in Outlook. Body may carry HTML.
const signaturePanelV2 = ({ heading, rows, origin }) => {
  const rowHtml = rows.map((r, i) => `
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;${i < rows.length - 1 ? 'margin:0 0 18px 0;' : ''}">
              <tr>
                <td width="36" valign="top" style="width:36px;padding-right:14px;font-size:0;line-height:0;">
                  ${origin ? `<img src="${escapeHtml(origin + '/icon-' + r.icon + '.png')}" width="36" height="36" alt="" style="display:block;border:0;outline:none;-ms-interpolation-mode:bicubic;width:36px;height:36px;" />` : '&nbsp;'}
                </td>
                <td valign="top" style="font-family:'Metropolis',Helvetica,Arial,sans-serif;">
                  <div style="font-size:14px;font-weight:700;line-height:20px;mso-line-height-rule:exactly;color:#131313;margin-bottom:2px;">${r.title}</div>
                  <div style="font-size:14px;line-height:22px;mso-line-height-rule:exactly;color:#4C4E42;">${r.body}</div>
                </td>
              </tr>
            </table>`).join('');
  return `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 8px 0;">
          <tr><td style="background:#EEF5E2;border:1px solid #DCE7CA;border-radius:14px;padding:24px 24px 22px 24px;">
            ${heading ? `<div style="font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:15px;font-weight:700;line-height:20px;mso-line-height-rule:exactly;color:#131313;margin:0 0 18px 0;">${escapeHtml(heading)}</div>` : ''}
            ${rowHtml}
          </td></tr>
        </table>`;
};

// Bulletproof green CTA — #92D400 fill, #131313 label, sentence case,
// 8px radius, VML fallback. Contrast 8.9:1 (dark on acid green).
const ctaV2 = ({ label, url }) => {
  const labelEsc = escapeHtml(label);
  const urlEsc = escapeHtml(url);
  return `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" class="sp-cta" style="width:100%;">
          <tr><td align="center" style="text-align:center;padding:28px 0 6px 0;">
            <!--[if mso]>
            <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${urlEsc}" style="height:50px;v-text-anchor:middle;width:260px;" arcsize="16%" stroke="f" fillcolor="#92D400">
              <w:anchorlock/>
              <center style="color:#131313;font-family:Helvetica,Arial,sans-serif;font-size:15px;font-weight:bold;">${labelEsc}</center>
            </v:roundrect>
            <![endif]-->
            <!--[if !mso]><!-- -->
            <a href="${urlEsc}" style="background-color:#92D400;border-radius:8px;color:#131313;display:inline-block;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:15px;font-weight:800;line-height:50px;text-align:center;text-decoration:none;width:260px;-webkit-text-size-adjust:none;mso-hide:all;">${labelEsc}</a>
            <!--<![endif]-->
          </td></tr>
        </table>`;
};

// Three-tier no-reply footer, BELOW the card (520px, centred). No
// "manage preferences" link (per #22).
const footerV2 = () => `
    <!--[if mso | IE]><table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" width="520"><tr><td><![endif]-->
    <table role="presentation" align="center" width="520" cellpadding="0" cellspacing="0" border="0" style="max-width:520px;width:100%;">
      <tr><td class="sp-foot" style="padding:28px 24px 8px 24px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid #D4D9C6;padding-top:24px;text-align:center;">
          <div style="font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:1.5px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#009159;margin-bottom:8px;">Need a hand?</div>
          <p style="margin:0 0 20px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:15px;line-height:22px;mso-line-height-rule:exactly;color:#9AA189;font-weight:600;"><a href="mailto:production@fusion-events.com" style="color:#009159;text-decoration:none;font-weight:600;">production@fusion-events.com</a> <span style="color:#B7BCA8;">&middot;</span> <span style="color:#009159;">01234&nbsp;351707</span></p>
          <p style="margin:0 0 14px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;mso-line-height-rule:exactly;color:#7C7F6F;">Sent by Supply Pilot on behalf of Fusion 2K.</p>
          <p style="margin:0 0 6px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:12px;line-height:18px;mso-line-height-rule:exactly;color:#9BA08C;">This inbox isn't monitored, so please don't reply to this email.</p>
          <p style="margin:0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;line-height:16px;mso-line-height-rule:exactly;color:#9BA08C;">&copy; 2026 Fusion 2K</p>
        </td></tr></table>
      </td></tr>
    </table>
    <!--[if mso | IE]></td></tr></table><![endif]-->`;

const renderTailV2 = () => `    </table>
    <!--[if mso | IE]></td></tr></table><![endif]-->
${footerV2()}
  </td></tr>
</table>
</body>
</html>`;

// Assemble a v2 supplier email: head + masthead + white body + footer.
// bodyInner is the email's body markup (greeting, copy, credential block,
// signature panel, CTA) — built by the caller with the shared helpers.
const supplierShellV2 = ({ preheader, bodyInner, ctaUrl }) => `${renderHeadV2(preheader)}
${mastheadV2(originFromUrl(ctaUrl))}
      <tr><td class="sp-body" bgcolor="#FFFFFF" style="background:#FFFFFF;padding:40px 48px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:15px;line-height:24px;mso-line-height-rule:exactly;color:#4C4E42;">
${bodyInner}
      </td></tr>
${renderTailV2()}`;

// Admin/internal V2 shell — the SAME black-masthead white-card chassis as
// supplierShellV2, so admin emails read identically to the supplier set. Only
// differences: the eyebrow is internal ("Supply Pilot", no "Partner Portal")
// and the Otto slot is present (opt-in via showOtto). Footer is the shared V2
// footer.
const alchemyShellV2 = ({ preheader, bodyInner, ctaUrl, showOtto = false }) => `${renderHeadV2(preheader)}
${mastheadV2(originFromUrl(ctaUrl), 'Supply Pilot')}
      <tr><td class="sp-body" bgcolor="#FFFFFF" style="background:#FFFFFF;padding:40px 48px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:15px;line-height:24px;mso-line-height-rule:exactly;color:#4C4E42;">
${bodyInner}
${showOtto ? renderOttoLight(ctaUrl) : ''}
      </td></tr>
${renderTailV2()}`;

// Tier 1 — Welcome. Strong Turbo voice, locked from the email-family
// mock. Body is deliberately short: greeting, "account's live", the
// username, password expectation, and a single Turbo flourish before
// the CTA. No corporate hedging ("excited", "onboard"), no exclamation
// marks. See [[project-email-family-rebuild]].
export const internalWelcomeEmail = ({ fullName, email, appUrl }) => {
  const firstRaw = fullName ? fullName.split(' ')[0] : '';
  const greeting = firstRaw ? `Hi ${escapeHtml(firstRaw)},` : 'Hi,';
  const textGreeting = firstRaw ? `Hi ${firstRaw},` : 'Hi,';
  // Authored natively in the light V2 idiom (credentialBlockV2 + ctaV2, ink /
  // deep-green tokens) so it no longer relies on the dark-card → light regex
  // adapter. Same short internal voice + Otto flourish as before.
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 22px 0;color:#131313;font-weight:600;">Account's live.</p>
${credentialBlockV2({ label: 'Username', value: email, mono: true })}
        <p style="margin:0 0 16px 0;">Password's on its way in a separate email — keep half an eye on your inbox.</p>
        <p style="margin:0 0 16px 0;">When you're ready to take it for a spin, the door's below.</p>
        <p style="margin:0 0 16px 0;">There's a <a href="${escapeHtml(appUrl)}/handbook" style="color:#009159;font-weight:600;">handbook</a> that explains the whole thing — the menu, the compliance rules, what suppliers see at their end. Worth ten minutes before you start clicking.</p>
        <p style="margin:0 0 28px 0;">See you on the inside.</p>
${ctaV2({ label: 'Sign in to Supply Pilot', url: appUrl })}`;
  const text = [
    textGreeting, '',
    "Account's live.", '',
    `Username: ${email}`, '',
    "Password's on its way in a separate email — keep half an eye on your inbox.",
    "When you're ready to take it for a spin, the door's below.", '',
    `The handbook explains the whole thing: ${appUrl}/handbook`, '',
    `Sign in to Supply Pilot: ${appUrl}`, '',
    'Need a hand? production@fusion-events.com · 01234 351707',
    "Sent by Supply Pilot on behalf of Fusion 2K. This inbox isn't monitored, so please don't reply.",
    '© 2026 Fusion 2K',
  ].join('\n');
  return {
    subject: "You're in. Welcome to Supply Pilot.",
    text,
    html: alchemyShellV2({
      preheader: "Account's live. Password's on its way in a separate email.",
      bodyInner,
      ctaUrl: appUrl,
      showOtto: true,
    }),
  };
};

export const welcomeEmail = ({ fullName, email, appUrl }) => {
  // Build brief #22 — rebuilt to the approved welcome mock on the v2
  // supplier email system (dark-green masthead + white logo, credential
  // block, light-green tips panel, three-tier no-reply footer).
  const firstRaw = fullName ? fullName.split(' ')[0] : '';
  const greeting = firstRaw ? `Hi ${escapeHtml(firstRaw)},` : 'Hi,';
  const textGreeting = firstRaw ? `Hi ${firstRaw},` : 'Hi,';
  const origin = originFromUrl(appUrl);
  const bodyInner = `
        <p style="margin:0 0 20px 0;font-size:16px;color:#131313;">${greeting}</p>
        <p style="margin:0 0 18px 0;">Welcome to Supply Pilot, Fusion 2K's partner portal. It's where we'll invite you to quote for briefs (our upcoming opportunities), where you'll send your responses, and where you'll keep your compliance documents current — all in one place.</p>
        <p style="margin:0 0 22px 0;color:#131313;font-weight:600;">Your account is now set up.</p>
${credentialBlockV2({ label: 'Username', value: email })}
        <p style="margin:0 0 28px 0;">Your password arrives in a separate email shortly. If it doesn't turn up, contact the Fusion 2K team member who set you up.</p>
${signaturePanelV2({ heading: 'A few things worth knowing before you start', origin, rows: [
    { icon: 'tour', title: 'Take the quick tour', body: 'The first time you log in, a short guided tour walks you through how briefs flow from invitation to awarded, and where everything lives. You can replay it any time from your account menu.' },
    { icon: 'info', title: 'Look for the &ldquo;i&rdquo; icons', body: "They're dotted next to key items throughout the portal — open one whenever you'd like a plain-English explanation (what a compliance status means, what each brief stage is, and so on)." },
    { icon: 'compliance', title: 'Start with compliance', body: 'Your first step is usually uploading your required documents, so Fusion 2K can begin awarding you work.' },
  ] })}
        <p style="margin:28px 0 20px 0;">When you're ready, head to the login page:</p>
${ctaV2({ label: 'Log in to Supply Pilot', url: appUrl })}`;
  const text = [
    textGreeting, '',
    "Welcome to Supply Pilot, Fusion 2K's partner portal. It's where we'll invite you to quote for briefs, send your responses, and keep your compliance documents current — all in one place.", '',
    'Your account is now set up.', '',
    `Username: ${email}`, '',
    "Your password arrives in a separate email shortly. If it doesn't turn up, contact the Fusion 2K team member who set you up.", '',
    'A few things worth knowing before you start:',
    '- Take the quick tour — it walks you through how briefs flow from invitation to awarded. Replay it any time from your account menu.',
    '- Look for the "i" icons — they explain key items in plain English.',
    '- Start with compliance — upload your required documents so Fusion 2K can begin awarding you work.', '',
    `Log in to Supply Pilot: ${appUrl}`, '',
    'Need a hand? production@fusion-events.com · 01234 351707',
    "Sent by Supply Pilot on behalf of Fusion 2K. This inbox isn't monitored, so please don't reply.",
    '© 2026 Fusion 2K',
  ].join('\n');
  return {
    subject: 'Welcome to Supply Pilot — your account is ready',
    text,
    html: supplierShellV2({
      preheader: "Your account's ready — here's how to get started.",
      bodyInner,
      ctaUrl: appUrl,
    }),
  };
};

// Tier 3 — Password. Plain professional voice, no Turbo. Future tense
// ("you will be prompted"), declarative imperatives ("do not share"),
// no warmth, no "happy flying" sign-off. The breach-contact footer
// gives the recipient a clear action if they didn't request the mail.
// See [[project-email-family-rebuild]].
export const internalPasswordEmail = ({ fullName, email, password, appUrl }) => {
  const firstRaw = fullName ? fullName.split(' ')[0] : '';
  const greeting = firstRaw ? `Hi ${escapeHtml(firstRaw)},` : 'Hi,';
  const textGreeting = firstRaw ? `Hi ${firstRaw},` : 'Hi,';
  // Authored natively in the light V2 idiom (credentialBlockV2 + ctaV2). Plain,
  // professional voice — no Otto. (The old footerText override was already
  // inert: the alchemyShell adapter never forwarded it, so the standard V2
  // footer has been rendering all along; dropped here to stop implying
  // otherwise.)
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 22px 0;">Here is your temporary password for Supply Pilot.</p>
${credentialBlockV2({ label: 'Temporary password', value: password, mono: true })}
        <p style="margin:0 0 16px 0;">You will be prompted to set your own password the first time you sign in.</p>
        <p style="margin:0 0 28px 0;">For security, do not share this password with anyone. Fusion staff will never ask for it.</p>
${ctaV2({ label: 'Sign in to Supply Pilot', url: appUrl })}`;
  const text = [
    textGreeting, '',
    'Here is your temporary password for Supply Pilot.', '',
    `Temporary password: ${password}`, '',
    'You will be prompted to set your own password the first time you sign in.',
    'For security, do not share this password with anyone. Fusion staff will never ask for it.', '',
    `Sign in to Supply Pilot: ${appUrl}`, '',
    "Sent by Supply Pilot on behalf of Fusion 2K. This inbox isn't monitored, so please don't reply.",
    '© 2026 Fusion 2K',
  ].join('\n');
  return {
    subject: "Your Supply Pilot password",
    text,
    html: alchemyShellV2({
      preheader: 'Here is your temporary password for Supply Pilot.',
      bodyInner,
      ctaUrl: appUrl,
      showOtto: false,
    }),
  };
};

export const passwordEmail = ({ fullName, email, password, appUrl }) => {
  // Build brief #22 — rebuilt to the approved password mock on the v2
  // supplier email system. Password stays LIVE, copyable, mono 18/700.
  const firstRaw = fullName ? fullName.split(' ')[0] : '';
  const greeting = firstRaw ? `Hi ${escapeHtml(firstRaw)},` : 'Hi,';
  const textGreeting = firstRaw ? `Hi ${firstRaw},` : 'Hi,';
  const origin = originFromUrl(appUrl);
  const bodyInner = `
        <p style="margin:0 0 20px 0;font-size:16px;color:#131313;">${greeting}</p>
        <p style="margin:0 0 22px 0;">Here's your password to sign in to Supply Pilot.</p>
${credentialBlockV2({ label: 'Password', value: password, mono: true })}
${signaturePanelV2({ origin, rows: [
    { icon: 'security', title: "Change this once you're in", body: "For your security, sign in and set a new password straight away. Please keep this email confidential and don't forward it to anyone." },
  ] })}
        <p style="margin:28px 0 20px 0;">Ready when you are:</p>
${ctaV2({ label: 'Log in to Supply Pilot', url: appUrl })}`;
  const text = [
    textGreeting, '',
    "Here's your password to sign in to Supply Pilot.", '',
    `Password: ${password}`, '',
    "Change this once you're in — for your security, sign in and set a new password straight away. Please keep this email confidential and don't forward it to anyone.", '',
    `Log in to Supply Pilot: ${appUrl}`, '',
    'Need a hand? production@fusion-events.com · 01234 351707',
    "Sent by Supply Pilot on behalf of Fusion 2K. This inbox isn't monitored, so please don't reply.",
    '© 2026 Fusion 2K',
  ].join('\n');
  return {
    subject: 'Your Supply Pilot password — sign in and change it',
    text,
    html: supplierShellV2({
      preheader: "Your password's inside — please change it once you're in.",
      bodyInner,
      ctaUrl: appUrl,
    }),
  };
};

export const supplierSetupFormEmail = ({ contactName, supplierName, formUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K would like to onboard <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> as a new supplier.</p>
    <p style="margin:0 0 16px 0;">Please complete the secure online form below so you can be set-up in our finance system.</p>
    <p style="margin:0 0 16px 0;">The link is unique to your business and can only be submitted once — keep it safe and complete it in one sitting where possible.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: 'Action required — Fusion 2K supplier set-up form',
    html: supplierShell({
      preheader: 'Please complete your supplier set-up form for Fusion 2K.',
      bodyHtml,
      ctaLabel: 'Open supplier set-up form',
      ctaUrl: formUrl,
    }),
  };
};

// A colleague at a supplier has been asked to complete or sign ONE document.
//
// The recipient has no relationship with Fusion 2K and may never have heard of
// Supply Pilot — they've been volunteered by a colleague. So the email leads
// with WHO asked and WHAT for, not with the product; says plainly whether
// they're signing or only filling in; and states the expiry, because a link
// that quietly stops working is worse than one that said when it would.
export const documentDelegationEmail = ({ inviteeName, inviterName, supplierName, documentLabel, scope, expiresOn, linkUrl }) => {
  const greeting = inviteeName ? `Hi ${escapeHtml(inviteeName.split(' ')[0])},` : 'Hello,';
  const signs = scope === 'complete_and_sign';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${escapeHtml(inviterName || 'A colleague')} at <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your company')}</strong> has asked you to ${signs ? 'complete and sign' : 'fill in'} a document for Fusion 2K:</p>
    <p style="margin:0 0 16px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    <p style="margin:0 0 16px 0;">${signs
      ? 'You&rsquo;ll be asked to read it, agree to each clause and sign it. It then goes to Fusion 2K for review.'
      : `You&rsquo;ll be asked to read it and fill in your part. You won&rsquo;t be signing it — it goes back to ${escapeHtml(inviterName || 'your colleague')} for that.`}</p>
    <p style="margin:0 0 16px 0;">You don&rsquo;t need an account. The link below is just for you and just for this document${expiresOn ? `, and it stops working on <strong style="color:#F9FCF1;">${escapeHtml(expiresOn)}</strong>` : ''}.</p>
    <p style="margin:0 0 16px 0;">If you weren&rsquo;t expecting this, please check with ${escapeHtml(inviterName || 'the person who sent it')} before opening it.</p>`;
  return {
    subject: `${signs ? 'Please sign' : 'Please complete'}: ${documentLabel}`,
    html: supplierShell({
      preheader: `${inviterName || 'A colleague'} has asked you to ${signs ? 'sign' : 'complete'} ${documentLabel}.`,
      bodyHtml,
      ctaLabel: signs ? 'Read and sign the document' : 'Open the document',
      ctaUrl: linkUrl,
    }),
  };
};

export const supplierFormCompletedEmail = ({ recipientName, supplierName, supplierUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#F9FCF1;">${escapeHtml(supplierName)}</strong> has completed their supplier set-up form. Their bank, registration and contact details are now on the supplier record and ready for Finance review.</p>`;
  return {
    subject: `${supplierName} has completed their set-up form`,
    html: alchemyShell({
      preheader: `${supplierName} has completed their set-up form.`,
      bodyHtml,
      ctaLabel: 'Review supplier in Supply Pilot',
      ctaUrl: supplierUrl,
      showTurbo: true,
    }),
  };
};

// Generic compliance-outstanding nudge — sent from the Compliance tab when
// onboarding documents are still outstanding. Intentionally does *not* list
// which documents are missing: the supplier sees the canonical list when
// they log into the portal, and listing them here means another place to
// keep in sync. Keep it short and direct.
export const complianceReminderEmail = ({ contactName, supplierName, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'Your account')}</strong> currently has outstanding compliance documents on the Fusion 2K supplier portal.</p>
    <p style="margin:0 0 16px 0;">Please log in to review the documents and complete and return them so your account can be cleared for ongoing work.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: 'Action required — outstanding compliance documents',
    html: supplierShell({
      preheader: 'Outstanding compliance documents on your Fusion 2K supplier portal.',
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Sent when staff activate a previously-inactive supplier and tick the
// "send a launch email" option in the activate modal. Tells the supplier
// they're now live on the panel and points them at the portal.
export const supplierActivationEmail = ({ contactName, supplierName, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">We're delighted to confirm that <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> is now live on the Fusion 2K supplier panel.</p>
    <p style="margin:0 0 16px 0;">You can sign in to your supplier portal at any time to view briefs we send your way, respond with quotes, and upload your compliance documents. Please log in soon and complete any compliance documents that are outstanding so we can start awarding work.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `${supplierName || 'Your business'} is now live on Fusion 2K Supply Pilot`,
    html: supplierShell({
      preheader: `${supplierName || 'Your business'} is now active on Fusion 2K Supply Pilot.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Per-document expiry warning — sent automatically by the daily cron when
// a document enters its 7-day pre-expiry window and no warning has been
// sent yet for this cycle. Mirror in api/_lib/brand-email-shell.cjs (the
// CJS copy the cron actually imports) — keep both in sync.
export const documentExpiringSoonEmail = ({ contactName, supplierName, documentLabel, expiryDate, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const expiryLine = expiryDate
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">Expires on:</span> <strong style="color:#E89A2E;">${escapeHtml(expiryDate)}</strong></p>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">A compliance document we hold for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> is about to expire and will need to be replaced:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${expiryLine}
    <p style="margin:0 0 16px 0;">Please log in to your Fusion 2K supplier portal and upload an up-to-date copy before the expiry date.</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#F9FCF1;">Important:</strong> Fusion 2K cannot award new work to any supplier whose compliance documents are not all up to date. Replacing this document ahead of expiry keeps your account cleared without interruption.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Action required — ${documentLabel} is about to expire`,
    html: supplierShell({
      preheader: `${documentLabel} is about to expire — please upload a replacement.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Per-document blast — sent from Admin > Risk Flag Configuration when staff
// publish a new compliance document and want every applicable active
// supplier to know they need to log in and complete it. Recipients are
// scoped on the call site (active suppliers whose risk flag matches one of
// the levels this doc is ticked for).
export const complianceDocPublishedEmail = ({ contactName, supplierName, documentLabel, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has just published a new compliance document that applies to <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong>:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    <p style="margin:16px 0;">Please log in to your supplier portal, review the document, and upload your signed copy so your account can be cleared for ongoing work.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Action required — new compliance document: ${documentLabel}`,
    html: supplierShell({
      preheader: `New compliance document on your Fusion 2K supplier portal — ${documentLabel}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// General "new document available" notice — sent when a non-compliance
// ('shown') document is published with Notify & publish. Informational (no
// upload/sign action, unlike complianceDocPublishedEmail); it just points the
// supplier at the portal library.
export const documentPublishedEmail = ({ contactName, documentLabel, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has just added a new document to your supplier portal:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    <p style="margin:16px 0;">Log in any time to read it in your portal document library. There's nothing you need to do right now — it's there for your reference.</p>`;
  return {
    subject: `New document available: ${documentLabel}`,
    html: supplierShell({
      preheader: `A new document is available in your Fusion 2K supplier portal — ${documentLabel}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Per-document expiry notice — sent automatically by the daily cron when a
// document's expiry_date has passed and no notice has been sent yet. Names the
// specific document so the supplier knows what to renew, and reinforces that
// no new work can be awarded until all compliance documents are up to date.
export const documentExpiredEmail = ({ contactName, supplierName, documentLabel, expiryDate, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const expiryLine = expiryDate
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">Expired on:</span> <strong style="color:#E89A2E;">${escapeHtml(expiryDate)}</strong></p>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">A compliance document we hold for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> has expired and needs to be replaced:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${expiryLine}
    <p style="margin:0 0 16px 0;">Please log in to your Fusion 2K supplier portal and upload an up-to-date copy at your earliest convenience.</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#F9FCF1;">Important:</strong> Fusion 2K cannot award new work to any supplier whose compliance documents are not all up to date. Replacing this document keeps your account cleared for ongoing and future briefs.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Action required — ${documentLabel} has expired`,
    html: supplierShell({
      preheader: `${documentLabel} has expired — please upload a replacement.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Sent to the supplier when a Fusion admin approves an uploaded
// compliance document via the new /compliance review queue. The
// supplier doesn't need to do anything — this is a "good news"
// confirmation. Uses supplierShell (Turbo-free, professional voice)
// because supplier emails never get the Turbo treatment, even for
// positive notifications. See [[project-email-shell-architecture]].
export const complianceDocumentApprovedEmail = ({ contactName, supplierName, documentLabel, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Good news — Fusion 2K has approved the compliance document we hold for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong>:</p>
    <p style="margin:0 0 16px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    <p style="margin:0 0 16px 0;">Your supplier portal will reflect the change next time you log in.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `${documentLabel} approved`,
    html: supplierShell({
      preheader: `${documentLabel} has been approved by Fusion 2K.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Sent when a NEW VERSION of a document a supplier has already signed needs
// signing again (document-versioning brief A §7). Two flavours, one template:
//
//   deadline  — they stay compliant until `deadline`, then they don't
//   immediate — they are not compliant from now
//
// The publish REASON is quoted verbatim: the supplier is being asked to re-read
// and re-sign something they already agreed to, so "what changed" is the whole
// justification for the ask. Also used by the 14/7/1-day reminders, which pass
// `reminderDaysLeft` so the same message can lead with the time remaining.
export const complianceResignRequiredEmail = ({
  contactName, supplierName, documentLabel, changeReason, deadline, immediate, reminderDaysLeft, portalUrl,
}) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';

  const deadlineLine = immediate
    ? `<p style="margin:0 0 16px 0;"><strong style="color:#E0290A;">This one takes effect immediately</strong> — until it's signed, Fusion 2K can't award ${escapeHtml(supplierName || 'your business')} new work.</p>`
    : deadline
      ? `<p style="margin:0 0 16px 0;">You're still compliant until <strong style="color:#F9FCF1;">${escapeHtml(deadline)}</strong>. After that date Fusion 2K can't award new work until the new version is signed.</p>`
      : '';

  const reasonBlock = changeReason
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #E89A2E;background:#FCF4E7;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;"><strong>What changed:</strong> ${escapeHtml(changeReason)}</td></tr></table>`
    : '';

  const lead = reminderDaysLeft != null
    ? `<p style="margin:0 0 16px 0;">A reminder — there ${reminderDaysLeft === 1 ? 'is <strong style="color:#F9FCF1;">1 day</strong>' : `are <strong style="color:#F9FCF1;">${reminderDaysLeft} days</strong>`} left to sign the updated version of this document:</p>`
    : `<p style="margin:0 0 16px 0;">Fusion 2K has published an updated version of a document ${escapeHtml(supplierName || 'your business')} has already signed, and it needs signing again:</p>`;

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    ${lead}
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${reasonBlock}
    ${deadlineLine}
    <p style="margin:0 0 16px 0;">Your previous signature stays on record — this is a new version, not a replacement of what you signed before.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;

  const subject = immediate
    ? `Action required — ${documentLabel} needs signing again`
    : reminderDaysLeft != null
      ? `Reminder — ${documentLabel} needs signing again${deadline ? ` by ${deadline}` : ''}`
      : `${documentLabel} has been updated — please sign again${deadline ? ` by ${deadline}` : ''}`;

  return {
    subject,
    html: supplierShell({
      preheader: `An updated version of ${documentLabel} needs signing.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// "You haven't come back to us yet" (detail-page brief §3). Deliberately NOT
// the chase email: a chase is for a supplier who never sent anything, and this
// one did — we asked them a question and they've gone quiet. The original ask
// is quoted so they don't have to go looking for what we wanted.
export const complianceNudgeEmail = ({ contactName, supplierName, documentLabel, question, askedOn, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const questionBlock = question
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #E89A2E;background:#FCF4E7;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(question)}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">We asked a question about a compliance document for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong>${askedOn ? ` on ${escapeHtml(askedOn)}` : ''}, and we don't think we've heard back:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${questionBlock}
    <p style="margin:0 0 16px 0;">You can reply on your supplier portal, or upload a replacement document there if that's easier. If you've already answered, thank you — nothing more is needed.</p>`;
  return {
    subject: `Still waiting on ${documentLabel}`,
    html: supplierShell({
      preheader: `A question about ${documentLabel} is still open.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// A reply from Fusion on a compliance document that ISN'T a decision — the
// answer to a supplier's question (detail-page brief §6). Nothing about their
// document changes; they just needed an answer, and a reply they're never told
// about isn't a reply.
export const complianceReplyEmail = ({ contactName, supplierName, documentLabel, message, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const messageBlock = message
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #2E8FA1;background:#EAF4F7;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(message)}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has replied about a compliance document for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong>:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${messageBlock}
    <p style="margin:0 0 16px 0;">Nothing has changed about the document itself — this is just the answer. The full conversation is on your supplier portal, where you can reply.</p>`;
  return {
    subject: `Fusion 2K replied — ${documentLabel}`,
    html: supplierShell({
      preheader: `A reply about ${documentLabel}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Sent when a Fusion admin clicks Request Info on a supplier upload
// via the new review queue. Always includes the admin's reason
// (preset label) + their notes verbatim — those drive the supplier's
// reply. CTA points at /portal/compliance where the document now
// shows in the Needs Clarification group with Reply/Replace buttons.
export const complianceClarificationRequestedEmail = ({ contactName, supplierName, documentLabel, reasonLabel, notes, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const reasonLine = reasonLabel
    ? `<p style="margin:0 0 8px 0;"><span style="color:#51534A;">Reason:</span> <strong style="color:#E89A2E;">${escapeHtml(reasonLabel)}</strong></p>`
    : '';
  const notesBlock = notes
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #E89A2E;background:#FCF4E7;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(notes)}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has reviewed a compliance document you uploaded for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> and asked for a bit more information:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${reasonLine}
    ${notesBlock}
    <p style="margin:0 0 16px 0;">Please log in to your supplier portal — your reply or replacement appears in the Needs Clarification section, with a Reply button for a text response or a Replace button to upload a new file.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Compliance — we need a bit more information on your ${documentLabel}`,
    html: supplierShell({
      preheader: `Fusion needs more information on your ${documentLabel}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Sent when a Fusion admin rejects a supplier upload. The doc goes
// back to Outstanding from the supplier's perspective and they must
// upload a replacement. Reason + notes carry the why.
export const complianceDocumentRejectedEmail = ({ contactName, supplierName, documentLabel, reasonLabel, notes, portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const reasonLine = reasonLabel
    ? `<p style="margin:0 0 8px 0;"><span style="color:#51534A;">Reason:</span> <strong style="color:#E0290A;">${escapeHtml(reasonLabel)}</strong></p>`
    : '';
  const notesBlock = notes
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #E0290A;background:#FBEEE9;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(notes)}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has reviewed a compliance document you uploaded for <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> and needs you to upload a replacement:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:1px;color:#F9FCF1;">${escapeHtml(documentLabel)}</p>
    ${reasonLine}
    ${notesBlock}
    <p style="margin:0 0 16px 0;">Please log in to your supplier portal and upload an updated copy. Until you do, this document is treated as outstanding for compliance purposes.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Compliance — your ${documentLabel} needs to be replaced`,
    html: supplierShell({
      preheader: `Fusion needs you to upload a new ${documentLabel}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// One "chase" email listing everything a supplier has outstanding —
// expired, rejected-awaiting-replacement, or never uploaded. Sent from the
// compliance review queue (§5), one email per supplier (never one per
// document). Each row carries a short status so the supplier knows why.
// What to ask the supplier to DO, given the mix of documents being chased.
// Not every compliance document is a file: a Code of Conduct is signed, a
// hosted form is completed. The old copy said "upload or renew" for all of
// them, which asked suppliers for a file that didn't exist. Falls back to the
// generic "action" wording as soon as the list is mixed — telling someone to
// sign an insurance certificate would be the same class of mistake.
const chaseActionPhrase = (documents = []) => {
  const verbs = new Set(documents.map(d => actionVerb(d.satisfied_by)));
  if (verbs.size === 1) {
    const only = verbs.values().next().value;
    if (only === 'upload') return 'upload or renew';
    if (only === 'sign') return 'read and sign';
    if (only === 'complete') return 'complete';
  }
  return 'action';
};

export const complianceChaseEmail = ({ contactName, supplierName, documents = [], portalUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const items = documents.map(d =>
    `<li style="margin:0 0 6px 0;"><strong style="color:#F9FCF1;">${escapeHtml(d.label)}</strong>${d.statusLabel ? ` <span style="color:#C9B98A;">— ${escapeHtml(d.statusLabel)}</span>` : ''}</li>`
  ).join('');
  const n = documents.length;
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">A quick reminder that <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> has ${n === 1 ? 'a compliance document' : 'compliance documents'} outstanding. Until ${n === 1 ? "it's" : "they're"} sorted, we can't put you forward for new work:</p>
    <ul style="margin:0 0 20px 0;padding-left:20px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:15px;line-height:24px;color:#E7ECDD;">${items}</ul>
    <p style="margin:0 0 16px 0;">Please log in to your supplier portal to ${chaseActionPhrase(documents)} ${n === 1 ? 'it' : 'them'}. If anything's unclear, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Compliance — ${n === 1 ? 'a document is' : `${n} documents are`} outstanding for ${supplierName || 'your business'}`,
    html: supplierShell({
      preheader: `You have ${n} compliance document${n === 1 ? '' : 's'} outstanding.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Reminder mail for an outstanding brief response — sent from BriefDetail
// when staff click "Send Reminder". Different copy from the initial
// notification (this isn't a new brief — the supplier already has it)
// and the deadline is the headline.
export const briefReminderEmail = ({ contactName, briefName, deadline, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const deadlineDisplay = fmtEmailDeadline(deadline);
  const deadlineLine = deadlineDisplay
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">Response required by:</span> <strong style="color:#E89A2E;">${escapeHtml(deadlineDisplay)}</strong></p>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">A quick reminder — Fusion 2K is still waiting for your response on the following brief:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:0.5px;color:#F9FCF1;">${escapeHtml(briefName)}</p>
    ${deadlineLine}
    <p style="margin:0 0 16px 0;">Please sign in to your supplier portal to view the brief and submit your response, or decline if you're unable to take it on.</p>
    <p style="margin:0 0 16px 0;">If you have any questions, contact the Fusion 2K team using the details below.</p>`;
  return {
    subject: `Reminder — ${briefName} response${deadline ? ` due ${deadline}` : ''}`,
    html: supplierShell({
      preheader: `Reminder: response required for ${briefName}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// Tier 2 — Deadline extended for a specific supplier (or set). Fired from
// BriefDetail's "Extend Deadline" modal when staff push a recipient's
// brief_recipients.response_deadline out and tick the Notify checkbox.
// Supplier-facing, supplierShell (Turbo-free), green accent — it's good
// news from the supplier's perspective. Optional `message` carries the
// operator's note straight into the body so the supplier sees why.
export const deadlineExtendedEmail = ({ contactName, briefName, newDeadline, message, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const deadlineDisplay = fmtEmailDeadline(newDeadline);
  const deadlineLine = deadlineDisplay
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">New response deadline:</span> <strong style="color:#92D400;">${escapeHtml(deadlineDisplay)}</strong></p>`
    : '';
  const messageBlock = message && message.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #92D400;background:#EEF6DE;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(message.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has extended your response deadline for the following brief:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:0.5px;color:#F9FCF1;">${escapeHtml(briefName)}</p>
    ${deadlineLine}
    ${messageBlock}
    <p style="margin:0 0 16px 0;">Please sign in to your supplier portal to submit (or update) your response before the new deadline.</p>`;
  return {
    subject: `Deadline extended — ${briefName}`,
    html: supplierShell({
      preheader: `Your response deadline for ${briefName} has been extended${deadlineDisplay ? ` to ${deadlineDisplay}` : ''}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// The answer to an extension request. ONE email for both outcomes, because
// from the supplier's side it is one answer to one question:
//
//   moved = false — the deadline stands, and they are told why.
//   moved = true  — a counter-offer. They asked for the 3rd, they are getting
//                   the 1st, and the email has to make that unambiguous.
//
// Sibling of deadlineExtendedEmail above and deliberately shaped the same way.
// That one covers granting exactly what was asked for; this covers everything
// else.
//
// THE DEADLINE IS ALWAYS RESTATED. "No" on its own leaves them working out what
// it means for them, which is how you lose a quote you could have had — and on
// a counter-offer the date IS the message. Same reason the note is required at
// the call site rather than optional here.
export const extensionAnsweredEmail = ({ contactName, briefName, deadline, moved, message, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const deadlineDisplay = fmtEmailDeadline(deadline);
  // Green when they gained time, amber when they did not — the colour carries
  // the outcome before the words are read.
  const accent = moved ? '#92D400' : '#E89A2E';
  const deadlineLine = deadlineDisplay
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">${moved ? 'Your new response deadline' : 'Response deadline, unchanged'}:</span> <strong style="color:${accent};">${escapeHtml(deadlineDisplay)}</strong></p>`
    : '';
  const messageBlock = message && message.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid ${accent};background:${moved ? '#EEF6DE' : '#FBF1E0'};"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(message.trim())}</td></tr></table>`
    : '';
  // "some more time" rather than "the extension you asked for": on a
  // counter-offer they did not get the date they asked for, and saying they did
  // is the kind of small dishonesty that costs trust when the date arrives.
  const opening = moved
    ? "Fusion 2K can't give you all the time you asked for on the brief below, but the deadline has been moved."
    : "Fusion 2K can't extend the response deadline for the brief below.";
  const closing = moved
    ? 'Please submit before the new deadline above.'
    : 'You can still respond — submit before the deadline above and your quote will be considered with the rest.';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${opening}</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:0.5px;color:#F9FCF1;">${escapeHtml(briefName)}</p>
    ${deadlineLine}
    ${messageBlock}
    <p style="margin:0 0 16px 0;">${closing}</p>`;
  return {
    subject: moved
      ? `New deadline — ${briefName}`
      : `Deadline can't be extended — ${briefName}`,
    html: supplierShell({
      preheader: moved
        ? `Your deadline for ${briefName} has moved${deadlineDisplay ? ` to ${deadlineDisplay}` : ''}.`
        : `Your extension request for ${briefName} wasn't granted${deadlineDisplay ? ` — the deadline stays ${deadlineDisplay}` : ''}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// ============================================================
// Supplier questions on briefs (§7 notifications)
// ============================================================

// To brief OWNERS — a supplier asked a question. Internal shell. Not a digest:
// an unanswered question has a cost that grows, so each one emails immediately.
export const briefQuestionAskedEmail = ({ ownerName, supplierName, briefName, questionBody, appUrl }) => {
  const greeting = ownerName ? `Hi ${escapeHtml(ownerName.split(' ')[0])},` : 'Hi,';
  const snippet = questionBody
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;"><tr><td style="border:1px solid #E6EADB;border-left:4px solid #009159;border-radius:8px;padding:14px 18px;background:#FBFCF8;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#131313;white-space:pre-wrap;">${escapeHtml(questionBody)}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong>${escapeHtml(supplierName || 'A supplier')}</strong> asked a question about <strong>${escapeHtml(briefName)}</strong>.</p>
    ${snippet}
    <p style="margin:0 0 16px 0;">Open the brief to answer them — privately, or shared with everyone quoting.</p>`;
  return {
    subject: `New question — ${briefName}`,
    html: alchemyShell({
      preheader: `${supplierName || 'A supplier'} asked a question about ${briefName}.`,
      bodyHtml,
      ctaLabel: 'Open the brief',
      ctaUrl: `${appUrl}/briefs`,
    }),
  };
};

// To suppliers still waiting — a holding update on a post-deadline brief:
// Fusion 2K now expects to decide by a given date (+ optional message). Replaces
// the "still no decision" chase state until the new date passes.
export const briefStatusUpdateEmail = ({ contactName, briefName, decisionBy, message, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const dateLine = decisionBy
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">Expected decision:</span> <strong style="color:#92D400;">${escapeHtml(decisionBy)}</strong></p>`
    : '';
  const msgBlock = message && message.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 16px 0;border-radius:12px;border-left:4px solid #92D400;background:#EEF6DE;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(message.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">A quick update on <strong style="color:#F9FCF1;">${escapeHtml(briefName)}</strong> — Fusion 2K is still deciding, and expects to have an answer for you soon.</p>
    ${dateLine}
    ${msgBlock}
    <p style="margin:0 0 16px 0;">No action needed from you — we'll be in touch either way.</p>`;
  return {
    subject: `Update — ${briefName}`,
    html: supplierShell({
      preheader: `Fusion 2K expects to decide on ${briefName}${decisionBy ? ` by ${decisionBy}` : ' soon'}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// To the ASKER — their question has been answered. Supplier shell.
export const briefQuestionAnsweredEmail = ({ contactName, briefName, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has answered your question about <strong style="color:#F9FCF1;">${escapeHtml(briefName)}</strong>.</p>
    <p style="margin:0 0 16px 0;">Sign in to your supplier portal to read the answer on the brief page.</p>`;
  return {
    subject: `Answered — your question on ${briefName}`,
    html: supplierShell({
      preheader: `Fusion 2K answered your question about ${briefName}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// To OTHER suppliers quoting — a clarification was shared (anonymously). Supplier
// shell. This is effectively an amendment to the brief, so it's worth an email.
export const briefQuestionPublishedEmail = ({ contactName, briefName, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has shared a clarification with everyone quoting on <strong style="color:#F9FCF1;">${escapeHtml(briefName)}</strong>. It may change what should be quoted.</p>
    <p style="margin:0 0 16px 0;">Please read it on the brief page before you price — and if you've already submitted, check whether your quote still stands.</p>`;
  return {
    subject: `Clarification shared — ${briefName}`,
    html: supplierShell({
      preheader: `A clarification was shared with everyone quoting on ${briefName}.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// To brief OWNERS — a supplier who quoted is asking for an update after the
// deadline passed with no outcome recorded. Internal shell. Immediate, not
// digested — the supplier may be holding crew/kit. The optional message is
// included verbatim (§5) because "we can hold until 8 Sep" is the useful part.
export const briefUpdateRequestEmail = ({ ownerName, supplierName, briefName, message, appUrl }) => {
  const greeting = ownerName ? `Hi ${escapeHtml(ownerName.split(' ')[0])},` : 'Hi,';
  const msgBlock = message && message.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;"><tr><td style="border:1px solid #E6EADB;border-left:4px solid #009159;border-radius:8px;padding:14px 18px;background:#FBFCF8;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#131313;white-space:pre-wrap;">${escapeHtml(message.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong>${escapeHtml(supplierName || 'A supplier')}</strong> is asking for an update on <strong>${escapeHtml(briefName)}</strong> — the response deadline has passed and no outcome is recorded yet.</p>
    ${msgBlock}
    <p style="margin:0 0 16px 0;">Open the brief to award it, close it, or reply — they may be holding capacity for the date.</p>`;
  return {
    subject: `Update requested — ${briefName}`,
    html: alchemyShell({
      preheader: `${supplierName || 'A supplier'} asked for an update on ${briefName}.`,
      bodyHtml,
      ctaLabel: 'Open the brief',
      ctaUrl: `${appUrl}/briefs`,
    }),
  };
};

// To the ASKER — their question was closed without an answer. Silence is worse.
export const briefQuestionClosedEmail = ({ contactName, briefName, appUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Your question about <strong style="color:#F9FCF1;">${escapeHtml(briefName)}</strong> has been closed without a specific answer — sometimes we can't say more at this stage.</p>
    <p style="margin:0 0 16px 0;">If it affects how you'd like to quote, sign in to your supplier portal and ask a follow-up, or get in touch using the details below.</p>`;
  return {
    subject: `Closed — your question on ${briefName}`,
    html: supplierShell({
      preheader: `Your question about ${briefName} was closed.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// Tier 3 — Compliance amber alert. Internal alert fired when a supplier
// whose compliance_status is amber or red is added to a brief. Sent to
// every internal user who has compliance_alert_emails=true. Plain
// professional voice, no Turbo, amber accent, outline-amber CTA.
//
// Optional `documents` and `eventDate` fields surface the mock-spec
// per-document status block when the call site has that data. Today
// the call site at src/lib/complianceAlert.js doesn't pass them — the
// template falls back gracefully with the lead line + cta only. Wiring
// the docs query into the call site is tracked as a follow-up after
// this template lands. See [[project-email-family-rebuild]].
//
// `addedByName` retained in the signature for backwards-compat with
// the existing call site but no longer surfaced in the body — the
// mock body opens with "supplier has been added to event" without
// naming the adder.
const DOC_STATUS_HEX = { green: '#92D400', amber: '#E89A2E', red: '#E0290A' };

export const complianceAlertOnBriefAddEmail = ({ recipientName, supplierName, complianceStatus, briefName, projectNumber, client, projectName, addedByName, briefUrl, documents, eventDate }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hello,';
  const statusUpper = String(complianceStatus || '').toUpperCase();
  const statusHex = complianceStatus === 'red' ? DOC_STATUS_HEX.red : DOC_STATUS_HEX.amber;
  const supplierEsc = escapeHtml(supplierName || 'A supplier');

  // Brief-details block — Project Number / Client / Project / Brief.
  // Renders below the opener so "the below brief" copy in the opener
  // has something concrete to reference. Each row only renders if its
  // value is present, so partially-populated briefs don't show empty
  // rows. Always renders the Brief row when a briefName exists.
  const briefRows = [
    { label: 'Project Number', value: projectNumber },
    { label: 'Client',         value: client },
    { label: 'Project',        value: projectName },
    { label: 'Brief',          value: briefName },
  ].filter(r => r.value);
  const briefDetailsBlock = briefRows.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${briefRows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:2px;line-height:18px;text-transform:uppercase;color:#51534A;width:38%;">${escapeHtml(row.label)}</td>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;">${escapeHtml(row.value)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';

  const docsBlock = Array.isArray(documents) && documents.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;border-left:3px solid ${statusHex};background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:${statusHex};">Status — ${escapeHtml(statusUpper)}</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${documents.map((doc, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            const valColour = DOC_STATUS_HEX[doc.statusColour] || '#F9FCF1';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;">${escapeHtml(doc.label || '')}</td>
            <td align="right" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:${valColour};font-weight:600;text-align:right;">${escapeHtml(doc.statusText || '')}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';

  const eventLine = eventDate
    ? `<p style="margin:0;">The event is scheduled for <strong style="color:#F9FCF1;">${escapeHtml(eventDate)}</strong>. Compliance status must be green before work can be awarded.</p>`
    : `<p style="margin:0;">Compliance status must be green before work can be awarded.</p>`;

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${supplierEsc} has been added to the below brief but their compliance status is currently <strong style="color:${statusHex};">${escapeHtml(statusUpper)}</strong>.</p>
    ${briefDetailsBlock}
    ${docsBlock}
    ${eventLine}
    <p style="margin:16px 0 0 0;font-size:13px;color:#DADCCD;">Compliance alerts can't be switched off.</p>`;

  return {
    subject: `Compliance alert — ${supplierName || 'supplier'} (${statusUpper}) added to ${briefName || 'a brief'}`,
    html: alchemyShell({
      preheader: `${supplierName || 'A supplier'} has been added to ${briefName || 'a brief'} with ${(complianceStatus || '').toLowerCase()} compliance status.`,
      bodyHtml,
      ctaLabel: 'Review compliance',
      ctaUrl: briefUrl,
    }),
  };
};

// Safety incident escalation — sent IMMEDIATELY (not digested) when a post-
// event feedback answer is "Reportable". Red accent. Goes to internal users
// with the safety-incident preference. The supplier's individual answers are
// NOT included — only that an incident was raised and its note.
export const safetyIncidentEmail = ({ recipientName, supplierName, briefName, projectNumber, note, raisedByName, supplierUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hello,';
  const supplierEsc = escapeHtml(supplierName || 'A supplier');
  const detailRows = [
    { label: 'Supplier', value: supplierName },
    { label: 'From brief', value: briefName },
    { label: 'Project Number', value: projectNumber },
    { label: 'Raised by', value: raisedByName },
  ].filter(r => r.value);
  const detailsBlock = detailRows.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 16px 0;border:1px solid #2a2a2a;border-left:3px solid ${DOC_STATUS_HEX.red};background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${detailRows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:2px;line-height:18px;text-transform:uppercase;color:#51534A;width:38%;">${escapeHtml(row.label)}</td>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;">${escapeHtml(row.value)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';
  const noteBlock = note
    ? `<p style="margin:0 0 16px 0;padding:10px 14px;background:#1a1a1a;border:1px solid #2a2a2a;border-radius:4px;font-style:italic;color:#DADCCD;">${escapeHtml(note)}</p>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">A <strong style="color:${DOC_STATUS_HEX.red};">reportable</strong> safety concern has been raised against ${supplierEsc} from post-event feedback.</p>
    ${detailsBlock}
    ${noteBlock}
    <p style="margin:0;">An incident has been recorded against them and their Safety recognition is paused until it's reviewed and closed. This doesn't block them from new work on its own — that's a separate decision.</p>
    <p style="margin:16px 0 0 0;font-size:13px;color:#DADCCD;">Safety incident alerts can't be switched off.</p>`;
  return {
    subject: `Safety incident — ${supplierName || 'a supplier'} (reportable)`,
    html: alchemyShell({
      preheader: `A reportable safety concern was raised against ${supplierName || 'a supplier'}.`,
      bodyHtml,
      ctaLabel: 'Review supplier',
      ctaUrl: supplierUrl,
    }),
  };
};

// Shared post-event feedback — a HUMAN-WRITTEN message to the supplier. The
// internal answers/notes are never included; the whole body is what the admin
// typed in the share box. Green, gentle, portal CTA.
export const supplierFeedbackShareEmail = ({ recipientName, supplierName, briefName, message, portalUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hello,';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Some feedback from the Fusion 2K team${briefName ? ` on <strong style="color:#F9FCF1;">${escapeHtml(briefName)}</strong>` : ''}:</p>
    <p style="margin:0;white-space:pre-wrap;color:#DADCCD;">${escapeHtml(message || '')}</p>`;
  return {
    subject: `Feedback from Fusion 2K${briefName ? ` — ${briefName}` : ''}`,
    html: alchemyShell({
      preheader: `Feedback from Fusion 2K${briefName ? ` on ${briefName}` : ''}.`,
      bodyHtml,
      ctaLabel: 'Open your portal',
      ctaUrl: portalUrl,
    }),
  };
};

// Post-event feedback — "ask someone else". An owner who wasn't close to the
// job hands the one-minute feedback form to a colleague who was. Internal,
// dark shell; the CTA drops them on the brief where the prompt is waiting.
export const feedbackRequestEmail = ({ recipientName, fromName, supplierName, briefName, message, briefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const actor = fromName ? escapeHtml(fromName) : 'A teammate';
  const supplierEsc = escapeHtml(supplierName || 'the supplier');
  const briefEsc = escapeHtml(briefName || 'a brief');
  const messageBlock = message
    ? `<p style="margin:0 0 16px 0;white-space:pre-wrap;color:#DADCCD;">“${escapeHtml(message)}”</p>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${actor} thinks you're better placed to say how <strong style="color:#F9FCF1;">${supplierEsc}</strong> did on <strong style="color:#F9FCF1;">${briefEsc}</strong>, and has asked you to give the post-event feedback.</p>
    ${messageBlock}
    <p style="margin:0;">It's four quick questions and takes about a minute — open the brief and you'll find the prompt at the top.</p>`;
  return {
    subject: `Can you give feedback on ${briefName || 'a brief'}?`,
    html: alchemyShell({
      preheader: `${fromName || 'A teammate'} asked you to give feedback on ${supplierName || 'a supplier'}.`,
      bodyHtml,
      ctaLabel: 'Give feedback',
      ctaUrl: briefUrl,
      showTurbo: true,
    }),
  };
};

// Tier 3 — Outstanding compliance digest. Weekly summary of every
// outstanding compliance item across the recipient's active events.
// Plain professional voice, amber accent, outline-amber CTA.
//
// Template only — the data-assembly and the weekly cron (Monday
// 09:00 GMT) are tracked as a separate follow-up. The function
// expects items already prepared by the caller; one row per
// outstanding document, so a supplier with two missing docs renders
// as two rows. The lead-line count is unique suppliers, derived
// from the items array. See [[project-email-family-rebuild]].
//
// `items` shape: [{ supplierName, documentLabel, statusText, statusColour }]
// where statusColour ∈ 'green' | 'amber' | 'red'.
export const outstandingComplianceDigestEmail = ({ recipientName, items = [], overviewUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hello,';
  const safeItems = Array.isArray(items) ? items : [];
  const supplierCount = new Set(safeItems.map(i => i.supplierName).filter(Boolean)).size;
  const supplierWord = supplierCount === 1 ? 'supplier' : 'suppliers';
  const verb = supplierCount === 1 ? 'has' : 'have';

  const itemsBlock = safeItems.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;border-left:3px solid #E89A2E;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#E89A2E;">Outstanding</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${safeItems.map((item, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            const valColour = DOC_STATUS_HEX[item.statusColour] || '#F9FCF1';
            const valText = [item.documentLabel, item.statusText].filter(Boolean).join(' · ');
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;">${escapeHtml(item.supplierName || '—')}</td>
            <td align="right" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:${valColour};font-weight:600;text-align:right;">${escapeHtml(valText)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${supplierCount} ${supplierWord} on your events ${verb} outstanding compliance documents.</p>
    ${itemsBlock}
    <p style="margin:0;">These documents are required before the relevant events. Please follow up with suppliers or chase via the compliance overview.</p>`;

  return {
    subject: 'Action required — outstanding compliance documents',
    html: alchemyShell({
      preheader: `${supplierCount} ${supplierWord} ${verb} outstanding compliance documents.`,
      bodyHtml,
      ctaLabel: 'Open compliance overview',
      ctaUrl: overviewUrl,
    }),
  };
};

// Sent to every non-declined recipient when an admin amends a brief
// post-send and ticks the "Notify recipients of these changes" option.
// Doesn't list a diff — keeping admins from having to think about
// per-field detail; the message blockquote (optional) carries any
// context they want to surface. Suppliers click through to the brief
// to see the latest details. Teal left-stripe on the message block to
// match the existing info/update tone in the product.
export const briefUpdatedEmail = ({ contactName, briefName, deadline, changeMessage, briefUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const deadlineDisplay = fmtEmailDeadline(deadline);
  const deadlineLine = deadlineDisplay
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">Response required by:</span> <strong style="color:#F9FCF1;">${escapeHtml(deadlineDisplay)}</strong></p>`
    : '';
  const messageBlock = changeMessage && changeMessage.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #2E8FA1;background:#E7F1F4;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(changeMessage.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has updated the following brief on Supply Pilot:</p>
    <p style="margin:0 0 16px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:0.5px;color:#F9FCF1;">${escapeHtml(briefName || 'Untitled brief')}</p>
    ${messageBlock}
    <p style="margin:0 0 16px 0;">Please open the brief in your supplier portal to re-review the latest details before submitting your response — or, if you've already submitted, to update what you sent.</p>
    ${deadlineLine}`;
  return {
    subject: `Update — ${briefName}`,
    html: supplierShell({
      preheader: `${briefName} has been updated.`,
      bodyHtml,
      ctaLabel: 'Open the brief',
      ctaUrl: briefUrl,
    }),
  };
};

export const briefNotificationEmail = ({ contactName, briefName, deadline, appUrl, briefType }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const deadlineDisplay = fmtEmailDeadline(deadline);
  const deadlineLine = deadlineDisplay
    ? `<p style="margin:0 0 16px 0;"><span style="color:#7C7F6F;">Response required by:</span> <strong style="color:#131313;">${escapeHtml(deadlineDisplay)}</strong></p>`
    : '';
  // Brief type framing — Exclusive earns a sentence in the opening to set
  // the supplier's expectation that they're the only one being asked.
  // Competitive earns a more subtle line so we don't telegraph price
  // pressure heavy-handedly. Unflagged briefs read exactly as before so
  // legacy briefs are unchanged.
  const typeIntroLine = briefType === 'exclusive'
    ? `<p style="margin:0 0 16px 0;">This is an <strong style="color:#009159;">exclusive brief</strong> — Fusion 2K has approached you specifically for this work.</p>`
    : briefType === 'competitive'
      ? `<p style="margin:0 0 16px 0;">This brief is being sent to multiple suppliers — please respond with your strongest offer.</p>`
      : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Fusion 2K has sent you a new brief on Supply Pilot:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;color:#131313;">${escapeHtml(briefName)}</p>
    ${typeIntroLine}
    ${deadlineLine}
    <p style="margin:0 0 16px 0;">Please sign in to the supplier portal to view the brief and submit your response.</p>`;
  // Only Exclusive earns a subject-line treatment — "Competitive" in the
  // subject reads as heavy-handed pressure. Unflagged keeps the legacy
  // subject so existing inboxes don't churn.
  const subject = briefType === 'exclusive'
    ? `You've been invited to respond to an exclusive brief: ${briefName}`
    : `New brief from Fusion 2K: ${briefName}`;
  return {
    subject,
    // Migrated off the BRIEF-17 light POC shell (white masthead) onto the v2
    // adapter so the brief invitation shares the black masthead + rebrand.
    html: supplierShell({
      preheader: briefType === 'exclusive'
        ? `Exclusive brief: ${briefName}`
        : `New brief: ${briefName}`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: `${appUrl}/portal`,
    }),
  };
};

// Tier 2 — Supplier responded. Turbo voice (less dramatic than Tier 1),
// "has responded" subject, "Their quote" info-block in the body.
//
// `coverage` and `attachmentCount` are optional — when passed they
// render as additional rows in the info-block. Today's call site at
// src/pages/SupplierResponse.js doesn't supply them, so the template
// falls back to a Total-only quote summary in the same shape. Wiring
// those fields through is a follow-up. See [[project-email-family-rebuild]].
export const supplierResponseEmail = ({ ownerName, supplierName, briefName, totalPrice, briefUrl, coverage, attachmentCount, isUpdate = false }) => {
  const greeting = ownerName ? `Hi ${escapeHtml(ownerName.split(' ')[0])},` : 'Hi,';
  const supplierEsc = escapeHtml(supplierName || 'A supplier');
  const briefEsc = escapeHtml(briefName || 'your brief');

  const rows = [];
  if (totalPrice != null && totalPrice !== '') {
    rows.push({ key: 'Total', val: `£${Number(totalPrice).toLocaleString()} ex-VAT` });
  }
  if (coverage) {
    rows.push({ key: 'Coverage', val: String(coverage) });
  }
  if (attachmentCount != null && attachmentCount !== '') {
    const n = Number(attachmentCount);
    rows.push({ key: 'Attachments', val: `${n} ${n === 1 ? 'document' : 'documents'}` });
  }

  const quoteBlock = rows.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#51534A;">Their quote</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${rows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;">${escapeHtml(row.key)}</td>
            <td align="right" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;text-align:right;">${escapeHtml(row.val)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';

  // A re-submission is a different event from a first response and needs to
  // read like one. "X has responded" arriving for the third time on the same
  // brief makes an owner think they missed something, or teaches them the
  // subject line doesn't mean anything. The figures may also have moved, which
  // matters more on an update than on a first quote.
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${supplierEsc} has ${isUpdate ? 'updated their response' : 'responded'} on <strong style="color:#F9FCF1;">${briefEsc}</strong>.</p>
    ${quoteBlock}
    <p style="margin:0;">${isUpdate
      ? 'These are their latest figures — the previous version is kept on the brief if you need to compare.'
      : "Full response is ready when you've got a minute."}</p>`;

  return {
    subject: isUpdate
      ? `${supplierName || 'A supplier'} has updated their response on ${briefName || 'your brief'}`
      : `${supplierName || 'A supplier'} has responded on ${briefName || 'your brief'}`,
    html: alchemyShell({
      preheader: isUpdate ? 'Updated quote — figures may have changed.' : "Quote's in and ready for review.",
      bodyHtml,
      ctaLabel: isUpdate ? 'View updated response' : 'View response',
      ctaUrl: briefUrl,
      showTurbo: true,
    }),
  };
};

// Brief-details block (Project Number / Client / Project / Brief) — the
// same table the compliance alert uses, so supplier-action notifications
// read as part of the one family. Rows with no value are omitted.
const briefDetailsTable = ({ projectNumber, client, projectName, briefName }) => {
  const rows = [
    { label: 'Project Number', value: projectNumber },
    { label: 'Client',         value: client },
    { label: 'Project',        value: projectName },
    { label: 'Brief',          value: briefName },
  ].filter(r => r.value);
  if (rows.length === 0) return '';
  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${rows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:2px;line-height:18px;text-transform:uppercase;color:#51534A;width:38%;">${escapeHtml(row.label)}</td>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;">${escapeHtml(row.value)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`;
};

// Supplier accepted a brief — internal notification to brief owners.
// Fusion-facing, so alchemyShell with Turbo (consistent with
// supplierResponseEmail). Carries the brief-details table for context,
// matching the compliance alert. Gated by the per-brief mute at the call site.
export const supplierAcceptedEmail = ({ ownerName, supplierName, briefName, projectNumber, client, projectName, briefUrl }) => {
  const greeting = ownerName ? `Hi ${escapeHtml(ownerName.split(' ')[0])},` : 'Hi,';
  const supplierEsc = escapeHtml(supplierName || 'A supplier');
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#131313;">${supplierEsc}</strong> has accepted the below brief and is working on a response.</p>
    ${briefDetailsTable({ projectNumber, client, projectName, briefName })}
    <p style="margin:0;">No action needed — we'll let you know when their quote lands.</p>`;
  return {
    subject: `${supplierName || 'A supplier'} accepted ${briefName || 'your brief'}`,
    // alchemyShell like every other admin email. This was the ONLY template on
    // alchemyShellLight, and alchemyShellLight renders no masthead — which is
    // why this one arrived without the black header band while everything else
    // had it. The "some have it, some don't" was one email, not a split family.
    // briefDetailsTable (the dark-authored one) goes with it: alchemyShell
    // recolours dark bodies for the light card, so the Light variant of the
    // table would have been double-lightened.
    html: alchemyShell({
      preheader: "They're in — quote to follow.",
      bodyHtml,
      ctaLabel: 'View brief',
      ctaUrl: briefUrl,
      showTurbo: true,
    }),
  };
};

// Supplier declined a brief — internal notification to brief owners.
// Amber accent, no Turbo (a decline isn't a celebration). Shows the
// brief-details table (matching the compliance alert) plus the reason
// category and any free-text note. Gated by the per-brief mute.
export const supplierDeclinedEmail = ({ ownerName, supplierName, briefName, projectNumber, client, projectName, reasonLabel, reasonNote, briefUrl }) => {
  const greeting = ownerName ? `Hi ${escapeHtml(ownerName.split(' ')[0])},` : 'Hi,';
  const supplierEsc = escapeHtml(supplierName || 'A supplier');

  const rows = [];
  if (reasonLabel) rows.push({ key: 'Reason', val: String(reasonLabel) });
  if (reasonNote) rows.push({ key: 'Note', val: String(reasonNote) });

  const reasonBlock = rows.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;border-left:3px solid ${ACCENT_HEX.amber};background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:${ACCENT_HEX.amber};">Why they declined</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${rows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td valign="top" style="padding:6px 12px 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;white-space:nowrap;">${escapeHtml(row.key)}</td>
            <td valign="top" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;">${escapeHtml(row.val)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#F9FCF1;">${supplierEsc}</strong> has declined the below brief.</p>
    ${briefDetailsTable({ projectNumber, client, projectName, briefName })}
    ${reasonBlock}
    <p style="margin:0;">You may want to line up another supplier.</p>`;

  return {
    subject: `${supplierName || 'A supplier'} declined ${briefName || 'your brief'}`,
    html: alchemyShell({
      preheader: 'A supplier has passed on this brief.',
      bodyHtml,
      ctaLabel: 'View brief',
      ctaUrl: briefUrl,
    }),
  };
};

// Supplier requested a deadline extension — internal notification to brief
// owners. Amber accent, no Turbo (it's a heads-up, not a celebration). The
// supplier doesn't propose a date; they just ask for more time. The CTA sends
// the owner to the brief, where the existing "Extend Deadline" tool grants it.
// Gated by the per-brief mute. Mirrors supplierDeclinedEmail's shape.
export const extensionRequestedEmail = ({ ownerName, supplierName, briefName, projectNumber, client, projectName, currentDeadline, note, briefUrl }) => {
  const greeting = ownerName ? `Hi ${escapeHtml(ownerName.split(' ')[0])},` : 'Hi,';
  const supplierEsc = escapeHtml(supplierName || 'A supplier');

  const rows = [];
  if (currentDeadline) rows.push({ key: 'Current deadline', val: String(currentDeadline) });
  if (note) rows.push({ key: 'Their note', val: String(note) });

  const detailBlock = rows.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;border-left:3px solid ${ACCENT_HEX.amber};background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:${ACCENT_HEX.amber};">Extension request</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${rows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td valign="top" style="padding:6px 12px 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;white-space:nowrap;">${escapeHtml(row.key)}</td>
            <td valign="top" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;">${escapeHtml(row.val)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;"><strong style="color:#F9FCF1;">${supplierEsc}</strong> has asked for more time to respond to the below brief.</p>
    ${briefDetailsTable({ projectNumber, client, projectName, briefName })}
    ${detailBlock}
    <p style="margin:0;">If you can accommodate it, grant a new deadline from the brief using <strong style="color:#F9FCF1;">Extend Deadline</strong>.</p>`;

  return {
    subject: `${supplierName || 'A supplier'} requested more time — ${briefName || 'your brief'}`,
    html: alchemyShell({
      preheader: 'A supplier has asked for a deadline extension.',
      bodyHtml,
      ctaLabel: 'View brief',
      ctaUrl: briefUrl,
    }),
  };
};

// Tier 2 — Brief awarded (internal confirmation to the awarder). Sent
// alongside the supplier-facing awardWinEmail / awardDeclineEmail
// fan-out when an admin awards a brief. Distinct from awardWinEmail:
// this one goes to the awarder in second person ("you've awarded"),
// where awardWinEmail is the supplier-facing congrats in third person.
// Turbo retained, green accent. See [[project-email-family-rebuild]].
// Sent to an internal user when they're added as an owner of a brief.
// Keeps the recipient informed without forcing them to discover the
// pin from the dashboard. `addedByName` is the actor; defaults to a
// generic phrasing when unknown (e.g. system-driven assignments).
export const briefOwnerAddedEmail = ({ recipientName, briefName, projectNumber, client, addedByName, briefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const briefEsc = escapeHtml(briefName || 'a brief');
  const actor = addedByName ? escapeHtml(addedByName) : 'A teammate';

  const rows = [
    { key: 'Brief', val: briefName || '—' },
  ];
  if (projectNumber) rows.push({ key: 'Project', val: projectNumber });
  if (client) rows.push({ key: 'Client', val: client });

  const summaryBlock = `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#51534A;">Brief</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${rows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;">${escapeHtml(row.key)}</td>
            <td align="right" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;text-align:right;">${escapeHtml(row.val)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`;

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">${actor} added you as an owner on <strong style="color:#F9FCF1;">${briefEsc}</strong>. It'll now show on your Pinned list and dashboard.</p>
    ${summaryBlock}
    <p style="margin:0;">Jump in when you're ready — supplier responses, comments, and amendments will all surface to you.</p>`;

  return {
    subject: `You've been added to ${briefName || 'a brief'}`,
    html: alchemyShell({
      preheader: `${addedByName || 'A teammate'} added you as an owner on ${briefName || 'a brief'}.`,
      bodyHtml,
      ctaLabel: 'Open brief',
      ctaUrl: briefUrl,
      showTurbo: true,
    }),
  };
};

export const briefAwardedInternalEmail = ({ awarderName, supplierName, briefName, awardedAmount, eventDate, briefUrl }) => {
  const greeting = awarderName ? `Hi ${escapeHtml(awarderName.split(' ')[0])},` : 'Hi,';
  const supplierEsc = escapeHtml(supplierName || 'the supplier');
  const briefEsc = escapeHtml(briefName || 'the brief');

  const rows = [
    { key: 'Supplier', val: supplierName || '—' },
    { key: 'Event', val: briefName || '—' },
  ];
  if (awardedAmount != null && awardedAmount !== '') {
    rows.push({ key: 'Value', val: `£${Number(awardedAmount).toLocaleString()} ex-VAT` });
  }
  if (eventDate) {
    rows.push({ key: 'Event date', val: String(eventDate) });
  }

  const awardBlock = `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;line-height:14px;mso-line-height-rule:exactly;text-transform:uppercase;color:#51534A;">Award summary</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${rows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#DADCCD;">${escapeHtml(row.key)}</td>
            <td align="right" style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;text-align:right;">${escapeHtml(row.val)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`;

  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">You've awarded <strong style="color:#F9FCF1;">${briefEsc}</strong> to <strong style="color:#F9FCF1;">${supplierEsc}</strong>.</p>
    ${awardBlock}
    <p style="margin:0;">The supplier has been notified. Anything that needs sorting will show up on the event page.</p>`;

  return {
    subject: `Brief awarded — ${supplierName || 'supplier'} have got the gig`,
    html: alchemyShell({
      preheader: `You've awarded ${briefName || 'a brief'} to ${supplierName || 'a supplier'}.`,
      bodyHtml,
      ctaLabel: 'View event',
      ctaUrl: briefUrl,
      showTurbo: true,
    }),
  };
};

// Award-win mail — sent to the awarded supplier on brief completion. The
// staff-authored personal message (if any) renders above the standard body
// so the supplier sees the human note first; the structured award summary
// follows. Net subtotal (ex-VAT) matches how all other Supply Pilot mail
// quotes prices post-Phase-C.
export const awardWinEmail = ({ contactName, supplierName, briefName, awardedAmount, personalMessage, briefUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const amountLine = awardedAmount != null && awardedAmount !== ''
    ? `<p style="margin:0 0 16px 0;"><span style="color:#51534A;">Awarded amount:</span> <strong style="color:#92D400;">£${escapeHtml(Number(awardedAmount).toLocaleString())}</strong> <span style="color:#51534A;">(ex-VAT)</span></p>`
    : '';
  const personalBlock = personalMessage && personalMessage.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #92D400;background:#EEF6DE;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(personalMessage.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Great news — <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong> has been awarded the following brief:</p>
    <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#92D400;">${escapeHtml(briefName)}</p>
    ${amountLine}
    ${personalBlock}
    <p style="margin:0 0 16px 0;">Your contact at Fusion 2K will be in touch shortly with next steps.</p>`;
  return {
    subject: `You've been awarded: ${briefName}`,
    html: supplierShell({
      preheader: `${supplierName || 'You'} have been awarded ${briefName}.`,
      bodyHtml,
      ctaLabel: 'Open brief in portal',
      ctaUrl: briefUrl,
    }),
  };
};

// Default decline copy. Exported so the Complete Brief panel can
// show admins exactly what every other-supplier will read, even
// when they don't add a personal note. Kept here as the single
// source of truth - the email template below uses the same string.
export const AWARD_DECLINE_DEFAULT_BODY =
  'Unfortunately, this brief has been awarded to another supplier on this occasion. We really appreciate the effort that went into your response and will be in touch about future opportunities.';

// Award-decline mail — sent to every responding supplier that didn't win,
// and (with reason swapped in) to every responding supplier when the brief
// is completed without award. The optional personalMessage block matches
// the award-win shape so the layout stays consistent across both outcomes.
export const awardDeclineEmail = ({ contactName, supplierName, briefName, body, personalMessage, briefUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  // Editable body (brief §4): an admin-written body replaces the default
  // decline paragraph; the greeting / brief line / shell stay locked. Falls
  // back to the default when nothing was written. `personalMessage` is the
  // legacy note-above-default path, honoured only when no explicit body.
  const mainText = (body && body.trim()) ? body.trim() : AWARD_DECLINE_DEFAULT_BODY;
  const personalBlock = (!body && personalMessage && personalMessage.trim())
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #51534A;background:#F2F4EA;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(personalMessage.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Thank you for responding to the following brief on behalf of <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong>:</p>
    <p style="margin:0 0 16px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:0.5px;color:#F9FCF1;">${escapeHtml(briefName)}</p>
    <p style="margin:0 0 16px 0;white-space:pre-wrap;">${escapeHtml(mainText)}</p>
    ${personalBlock}`;
  return {
    subject: `Update on ${briefName}`,
    html: supplierShell({
      preheader: `Update on ${briefName} from Fusion 2K.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: briefUrl,
    }),
  };
};

// Closed-without-award mail — sent to every responding supplier when the
// brief is completed without picking a winner. Tone is neutral: the brief
// simply isn't going ahead. Same personal-message block as decline.
// Plain-text of the default close prose, surfaced in the admin close panel
// (CompleteBriefPanel) so the sender sees exactly what every supplier reads
// when a brief is closed without award. Kept in sync with the body below.
export const BRIEF_CLOSE_DEFAULT_BODY =
  'This brief is no longer going ahead and has now been closed. We really appreciate your response and will be in touch about future opportunities.';

export const briefClosedEmail = ({ contactName, supplierName, briefName, personalMessage, briefUrl }) => {
  const greeting = contactName ? `Hi ${escapeHtml(contactName.split(' ')[0])},` : 'Hello,';
  const personalBlock = personalMessage && personalMessage.trim()
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border-radius:12px;border-left:4px solid #51534A;background:#F2F4EA;"><tr><td style="padding:14px 18px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;white-space:pre-wrap;">${escapeHtml(personalMessage.trim())}</td></tr></table>`
    : '';
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">${greeting}</p>
    <p style="margin:0 0 16px 0;">Thank you for the time you spent responding to the following brief on behalf of <strong style="color:#F9FCF1;">${escapeHtml(supplierName || 'your business')}</strong>:</p>
    <p style="margin:0 0 16px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;letter-spacing:0.5px;color:#F9FCF1;">${escapeHtml(briefName)}</p>
    <p style="margin:0 0 16px 0;">${BRIEF_CLOSE_DEFAULT_BODY}</p>
    ${personalBlock}`;
  return {
    subject: `${briefName} has been closed`,
    html: supplierShell({
      preheader: `${briefName} has been closed by Fusion 2K.`,
      bodyHtml,
      ctaLabel: 'Open supplier portal',
      ctaUrl: briefUrl,
    }),
  };
};

// Password reset emails are sent by Supabase Auth, not our SendGrid pipeline.
// This function renders the branded HTML body with Supabase's Go template tokens
// ({{ .ConfirmationURL }}). The output lives in the Supabase dashboard under
// Authentication → Email Templates → Reset Password — see
// email-templates/supabase-password-reset.html for the rendered version to paste.
export const passwordResetEmail = () => {
  const bodyHtml = `
    <p style="margin:0 0 16px 0;">Hiya,</p>
    <p style="margin:0 0 16px 0;">Please follow the below link to reset your password:</p>`;
  return {
    subject: 'Fusion 2K Supply Pilot — Password Reset',
    html: supplierShell({
      preheader: 'Reset your Supply Pilot password.',
      bodyHtml,
      ctaLabel: 'Reset password',
      ctaUrl: '{{ .ConfirmationURL }}',
    }),
  };
};

// Inline-styled comment HTML renderer for emails. Mirrors the cron's
// renderCommentHtml (api/cron/comment-mention-notifications.js) so the
// client-side inline path produces byte-identical body markup. Inline
// styles instead of CSS classes because email clients strip <style>.
// Handles: @[u:uuid:name] mentions, [label](url) links, **bold**, *italic*,
// double-newline → paragraph breaks, single newline → <br>.
const MENTION_RE_EMAIL = /@\[u:([0-9a-f-]{36}):([^\]]+)\]/g;
const PH_OPEN_EMAIL = '';
const PH_CLOSE_EMAIL = '';
const PH_TOKEN_RE_EMAIL = new RegExp(`${PH_OPEN_EMAIL}(\\d+)${PH_CLOSE_EMAIL}`, 'g');
const safeHrefEmail = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '#';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) return trimmed;
  return '#';
};
export function renderCommentHtmlForEmail(content) {
  if (!content) return '';
  const placeholders = [];
  const stash = (html) => {
    const idx = placeholders.length;
    placeholders.push(html);
    return `${PH_OPEN_EMAIL}${idx}${PH_CLOSE_EMAIL}`;
  };
  let text = String(content || '');
  text = text.replace(MENTION_RE_EMAIL, (_, _uuid, display) => stash(
    `<span style="color:#2E8FA1;background:rgba(46,143,161,0.12);padding:1px 6px;border-radius:4px;font-weight:700;">@${escapeHtml(display)}</span>`
  ));
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) => stash(
    `<a href="${escapeHtml(safeHrefEmail(url))}" target="_blank" rel="noopener noreferrer" style="color:#2E8FA1;text-decoration:underline;">${escapeHtml(label)}</a>`
  ));
  text = text.replace(/\*\*([\s\S]+?)\*\*/g, (_, inner) => stash(`<strong style="color:#F9FCF1;">${escapeHtml(inner)}</strong>`));
  text = text.replace(/\*([\s\S]+?)\*/g, (_, inner) => stash(`<em style="color:#F9FCF1;font-style:italic;">${escapeHtml(inner)}</em>`));
  let escaped = escapeHtml(text);
  escaped = escaped.replace(PH_TOKEN_RE_EMAIL, (_, idx) => placeholders[Number(idx)] || '');
  const paragraphs = escaped.split(/\n{2,}/).map(p => p.replace(/\n/g, '<br>'));
  return `<p style="margin:0 0 8px 0;">${paragraphs.join('</p><p style="margin:0 0 8px 0;">')}</p>`;
}

// ============================================================================
// Debrief review (debrief-review-approval brief §6)
// ============================================================================
// Four emails, one per transition that lands in somebody else's lap. There is
// deliberately NO email for "withdrawn" — the author withdrew their own
// debrief, so the only person who would learn anything already knows.
//
// All four are authored natively in the light V2 idiom (ink #131313, deep
// green #009159, ctaV2) rather than the dark-body regex adapter.

const debriefRowsV2 = (rows) => `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #E6EADB;background:#F7F9F0;">
          <tr><td style="padding:12px 14px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
${rows.filter(r => r.val).map((row, i) => `              <tr>
                <td style="padding:6px 0 4px 0;${i === 0 ? '' : 'border-top:1px solid #E6EADB;'}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#4C4E42;">${escapeHtml(row.key)}</td>
                <td align="right" style="padding:6px 0 4px 0;${i === 0 ? '' : 'border-top:1px solid #E6EADB;'}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#131313;font-weight:600;text-align:right;">${escapeHtml(row.val)}</td>
              </tr>`).join('\n')}
            </table>
          </td></tr>
        </table>`;

// A reviewer's note, shown as a quote rather than a paragraph — it is somebody
// else's words and shouldn't read as if the system wrote it.
const debriefNoteV2 = (note) => `
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;">
          <tr>
            <td width="3" style="background:#009159;"></td>
            <td style="padding:2px 0 2px 14px;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:22px;color:#4C4E42;font-style:italic;">${escapeHtml(note)}</td>
          </tr>
        </table>`;

const debriefMeta = ({ projectRef, projectName, client }) => [
  { key: 'Project', val: projectName },
  { key: 'Reference', val: projectRef },
  { key: 'Client', val: client },
];

// → every admin. Somebody is waiting on a read.
export const debriefSubmittedEmail = ({ recipientName, authorName, projectRef, projectName, client, debriefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const author = escapeHtml(authorName || 'Someone');
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 20px 0;">${author} submitted a debrief for approval. It won't be visible to the team until an admin approves it.</p>
${debriefRowsV2(debriefMeta({ projectRef, projectName, client }))}
        <p style="margin:0 0 24px 0;">You can approve it or send it back with a note. It'll also sit in <strong style="color:#131313;">Awaiting review</strong> on the Briefs page until someone does.</p>
${ctaV2({ label: 'Review debrief', url: debriefUrl })}`;
  return {
    subject: `Debrief to review — ${projectName || projectRef || 'a project'}`,
    html: alchemyShellV2({
      preheader: `${authorName || 'Someone'} submitted a debrief for approval.`,
      bodyInner,
      ctaUrl: debriefUrl,
    }),
  };
};

// → the author. The note is the payload; without it this email is useless, so
// it is required at the call site rather than defaulted to something bland.
export const debriefChangesRequestedEmail = ({ recipientName, reviewerName, note, projectRef, projectName, client, debriefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const reviewer = escapeHtml(reviewerName || 'A reviewer');
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 18px 0;">${reviewer} has sent your debrief back for changes. It's a draft again, so you can edit it and resubmit.</p>
${debriefNoteV2(note || '')}
${debriefRowsV2(debriefMeta({ projectRef, projectName, client }))}
${ctaV2({ label: 'Open debrief', url: debriefUrl })}`;
  return {
    subject: `Changes requested — ${projectName || projectRef || 'your debrief'}`,
    html: alchemyShellV2({
      preheader: `${reviewerName || 'A reviewer'} sent your debrief back with a note.`,
      bodyInner,
      ctaUrl: debriefUrl,
    }),
  };
};

// → the author, on approval. Separate from the action-owner email below
// because they are answering different questions: "is my write-up done?" and
// "what have I been left to do?".
export const debriefApprovedEmail = ({ recipientName, approverName, selfApproved = false, projectRef, projectName, client, debriefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const line = selfApproved
    ? 'You approved your own debrief, so it is now published and visible to the team.'
    : `${escapeHtml(approverName || 'An admin')} approved your debrief. It's published and visible to the team.`;
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 20px 0;">${line}</p>
${debriefRowsV2(debriefMeta({ projectRef, projectName, client }))}
        <p style="margin:0 0 24px 0;">Anyone owning an action from it has been told separately.</p>
${ctaV2({ label: 'Open debrief', url: debriefUrl })}`;
  return {
    subject: `Debrief published — ${projectName || projectRef || 'your project'}`,
    html: alchemyShellV2({
      preheader: 'Your debrief is published and visible to the team.',
      bodyInner,
      ctaUrl: debriefUrl,
      // Otto appears on the good-news admin emails (awarded, accepted, a
      // supplier responded) and stays away from alerts and chases. This is the
      // only one of the five debrief emails that is somebody's work landing
      // well, so it is the only one that gets him.
      showOtto: true,
    }),
  };
};

// → each action owner who is a Supply Pilot user. Their actions are listed in
// full: an email that says "you have actions" and makes you click to find out
// what they are has wasted the trip.
export const debriefActionsAssignedEmail = ({ recipientName, actions = [], projectRef, projectName, client, debriefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const n = actions.length;
  // The action IS the note (project-debriefs §3) — debrief_actions carries no
  // text of its own, so the body arrives on the joined note. `timeframe` is
  // deliberately free text ("before the next show", "end of Q3"), not a date,
  // so it is printed as written rather than run through a date formatter.
  const items = actions.map(a => `
              <tr>
                <td style="padding:8px 0;border-top:1px solid #E6EADB;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;line-height:21px;color:#131313;">
                  ${escapeHtml(a.note?.body || a.description || 'Action')}
                  ${a.timeframe ? `<div style="font-size:12px;line-height:18px;color:#6B6E5F;padding-top:2px;">${escapeHtml(a.timeframe)}</div>` : ''}
                </td>
              </tr>`).join('');
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 18px 0;">A debrief has been published and ${n === 1 ? 'an action is' : `${n} actions are`} down to you.</p>
${debriefRowsV2(debriefMeta({ projectRef, projectName, client }))}
        <p style="margin:0 0 6px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:#6B6E5F;">Your action${n === 1 ? '' : 's'}</p>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 24px 0;">${items}
        </table>
${ctaV2({ label: 'Open debrief', url: debriefUrl })}`;
  return {
    subject: n === 1
      ? `An action for you — ${projectName || projectRef || 'a debrief'}`
      : `${n} actions for you — ${projectName || projectRef || 'a debrief'}`,
    html: alchemyShellV2({
      preheader: `${n === 1 ? 'An action is' : `${n} actions are`} down to you from a published debrief.`,
      bodyInner,
      ctaUrl: debriefUrl,
    }),
  };
};

// → the author, when a published debrief is pulled back to draft. Sent because
// the author is the one person who might still be pointing people at it.
export const debriefUnpublishedEmail = ({ recipientName, actorName, projectRef, projectName, client, debriefUrl }) => {
  const greeting = recipientName ? `Hi ${escapeHtml(recipientName.split(' ')[0])},` : 'Hi,';
  const actor = escapeHtml(actorName || 'An admin');
  const bodyInner = `
        <p style="margin:0 0 16px 0;">${greeting}</p>
        <p style="margin:0 0 20px 0;">${actor} unpublished your debrief. It's back to a draft, so it's no longer visible to the team and can be edited and resubmitted.</p>
${debriefRowsV2(debriefMeta({ projectRef, projectName, client }))}
${ctaV2({ label: 'Open debrief', url: debriefUrl })}`;
  return {
    subject: `Debrief unpublished — ${projectName || projectRef || 'your project'}`,
    html: alchemyShellV2({
      preheader: `${actorName || 'An admin'} pulled your debrief back to a draft.`,
      bodyInner,
      ctaUrl: debriefUrl,
    }),
  };
};

// Internal Tier 1 mention email. Originally rendered as a batched
// digest by the comment-mention-notifications cron (5-min window).
// 2026-06: the cron stopped firing on production and the queue
// silently accumulated 14 days of unsent rows — switched to inline
// send from BriefCommentsPanel.js so every mention fires the moment
// the comment posts. The `comments` array is now always length 1
// per send (no batching) — the multi-comment digest plumbing stays
// in place for any future surface that wants it.
export const commentMentionEmail = ({ recipientName, briefName, projectNumber, client, projectName, briefUrl, comments }) => {
  const safeRecipient = (recipientName || '').split(/\s+/)[0] || 'there';
  const list = Array.isArray(comments) ? comments : [];
  const n = list.length;
  // Display name for the brief in copy/subject. briefName is the proper
  // brief title; fall back to the project name before the bare label so
  // we never echo the raw brief UUID at the recipient.
  const briefLabel = briefName || projectName || 'a brief';
  const subject = n > 1
    ? `You were mentioned on "${briefLabel}" (${n} comments)`
    : `You were mentioned on "${briefLabel}"`;
  const summaryAuthors = Array.from(new Set(list.map(c => c.authorName).filter(Boolean)));
  const authorSummary = summaryAuthors.length === 0
    ? 'A teammate'
    : summaryAuthors.length === 1
      ? summaryAuthors[0]
      : summaryAuthors.length === 2
        ? `${summaryAuthors[0]} and ${summaryAuthors[1]}`
        : `${summaryAuthors[0]} and ${summaryAuthors.length - 1} others`;
  // Brief-details block — Project Number / Client / Project / Brief.
  // Mirrors complianceAlertOnBriefAddEmail so the mention email surfaces
  // the same concrete header the recipient sees in-app, instead of the
  // brief id. Each row only renders if its value is present, so
  // partially-populated briefs don't show empty rows.
  const briefRows = [
    { label: 'Project Number', value: projectNumber },
    { label: 'Client',         value: client },
    { label: 'Project',        value: projectName },
    { label: 'Brief',          value: briefName },
  ].filter(r => r.value);
  const briefDetailsBlock = briefRows.length > 0
    ? `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin:0 0 20px 0;border:1px solid #2a2a2a;background:#1a1a1a;">
      <tr><td style="padding:10px 14px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;">
          ${briefRows.map((row, i) => {
            const rowBorder = i === 0 ? '' : 'border-top:1px solid #232323;';
            return `<tr>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:2px;line-height:18px;text-transform:uppercase;color:#51534A;width:38%;">${escapeHtml(row.label)}</td>
            <td style="padding:6px 0 4px 0;${rowBorder}font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:13px;line-height:20px;color:#F9FCF1;font-weight:600;">${escapeHtml(row.value)}</td>
          </tr>`;
          }).join('')}
        </table>
      </td></tr>
    </table>`
    : '';
  const blocks = list.map(c => `
    <div style="margin:0 0 18px 0;padding:14px 16px;background:#1a1a1a;border:1px solid #2a2a2a;border-radius:10px;">
      <p style="margin:0 0 8px 0;font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:12px;color:#92D400;font-weight:700;letter-spacing:1px;text-transform:uppercase;">${escapeHtml(c.authorName || 'Fusion')}<span style="color:#51534A;font-weight:400;letter-spacing:0;text-transform:none;"> · ${escapeHtml(c.createdAtDisplay || '')}</span></p>
      <div style="font-family:'Metropolis',Helvetica,Arial,sans-serif;font-size:14px;color:#DADCCD;line-height:1.6;">${c.html || escapeHtml(c.content || '')}</div>
    </div>
  `).join('');
  const bodyHtml = `
    <p style="margin:0 0 12px 0;">Hi ${escapeHtml(safeRecipient)},</p>
    <p style="margin:0 0 18px 0;">${escapeHtml(authorSummary)} mentioned you in ${n} comment${n === 1 ? '' : 's'} on <strong style="color:#F9FCF1;">${escapeHtml(briefLabel)}</strong>.</p>
    ${briefDetailsBlock}
    ${blocks}`;
  return {
    subject,
    html: alchemyShell({
      preheader: `${authorSummary} mentioned you on "${briefLabel}".`,
      bodyHtml,
      ctaLabel: 'View brief',
      ctaUrl: briefUrl,
      showTurbo: true,
    }),
  };
};

// Sends an email via the /api/send-email serverless function. THROWS on
// any non-2xx response or network error so callers' try/catch blocks
// correctly distinguish success from failure.
//
// Earlier this returned `{ ok: false }` on failure WITHOUT throwing.
// Every existing caller wraps sendEmail in try/catch but none reads the
// return value, so 502 / 500 / network errors were silently treated as
// successful sends — fan-outs incremented their "sent" counters and
// users saw green confirmation toasts while nothing left SendGrid.
//
// Contract: resolves on success; throws Error on failure. Callers
// MUST wrap in try/catch and either log+continue (best-effort
// fan-outs) or surface the failure (single user-facing sends).
export const sendEmail = async ({ to, subject, html, text }) => {
  let res;
  // The endpoint requires a logged-in caller (it was an open relay until
  // 2026-08-14 — see the note at the top of api/send-email.js). Every surface
  // that sends mail is behind a login, so a missing session here means
  // something is wrong rather than something is public: fail with a clear
  // message instead of a bare 401 from the server.
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('Not signed in — cannot send email');
  }
  try {
    res = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      // text = plain-text alternative (BRIEF-22 §5). Optional; omitted
      // when a caller doesn't supply one.
      body: JSON.stringify({ to, subject, html, ...(text ? { text } : {}) }),
    });
  } catch (err) {
    console.warn('Email send threw', { to, subject, error: err?.message });
    throw new Error(err?.message || 'Network error reaching email service');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    console.warn('Email send failed', { to, subject, status: res.status, body });
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return { ok: true };
};
